/* eslint-disable max-len */
import {
  Autocomplete,
  Box,
  CircularProgress,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Dialog } from '@/components/common/Dialog';
import { PropertyAddressMapCard } from '@/components/common/PropertyAddressMapCard';
import {
  Button as DsButton,
  Chip,
  CloseButton,
  Divider as DsDivider,
  Field,
  Icon,
  IconButton as DsIconButton,
  Segmented,
  SortCaret,
  Tag,
  TextInput,
} from '@/components/ds';
import { LISTING_STATUS_COLOR } from '@/constants/listingStatus';
import { useAuthContext } from '@/context/AuthProvider';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { AdminProperty, useAdminProperties } from '@/hooks/admin/useAdminProperties';
import { useAdminUsers } from '@/hooks/admin/useAdminUsers';
import {
  useAdminClientGroupsSearch,
  useClientGroups,
  useCreateClientGroup,
  useCreateDeal,
  useCreateListing,
  useCreateMember,
  useDeleteClientGroup,
  useDeleteListing,
  useDeleteMember,
  useUpdateClientGroup,
  useUpdateDeal,
  useUpdateListing,
  useUpdateMember,
} from '@/hooks/useClientGroups';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { usePropertyRequestsByGroup, useResolvePropertyRequest } from '@/hooks/usePropertyRequests';
import { useUserRole } from '@/hooks/useUserRole';
import { transferProperty } from '@/services/api/admin';
import { createDocuments } from '@/services/api/documents/api';
import { generatePreSignedUrls } from '@/services/api/files';
import { resendInviteByEmail } from '@/services/api/invites';
import { checkPropertyExists, createProperty } from '@/services/api/properties/api';
import { useTranslationPrefix } from '@/translations/i18n';
import {
  Deal as ApiDeal,
  ClientGroup,
  DealFeeType,
  DealListing,
  GroupMember,
  UpdateDealRequest,
} from '@/types/clientGroups';
import { fetchAddressSuggestions, parseGoogleAddressComponents } from '@/types/maps';
import {
  buildDealFeePayload,
  calculateAutomaticFlatFee,
  calculateRevenueForDeal,
  calculateRevenueForDeals,
  isValidCommissionPercent,
} from '@/utils/dealFees';
import { epochSecondsToLocalDateStr, formatLocalDate, parseLocalDate } from '@/utils/formatDate';
import { formatPhone } from '@/utils/formatPhone';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  input: 'var(--tx-fill)',
  border: 'var(--tx-line)',
  borderLight: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  danger: '#ff4d4d',
  amber: '#f0a500',
};

const L = {
  critical: '#DB5282',
  urgent: '#FF9A4D',
  moderate: '#FFF981',
  low: '#5AE9FF',
  monitor: '#9A938A',
  resolved: '#C0FF02',
};
const font = "'Manrope', sans-serif";

interface PendingPropertyRequest {
  id: string;
  buyerUserId: string;
  buyerName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
  createdAt: number;
  requestType?: 'group_access' | 'ownership_claim';
}

// Shared master grid: chevron | name | co-advisor | status | deals | offers | expires | fee
//
// Every column except `name` is a single fixed width so each row's grid
// container resolves identical track widths and rows visually align as a
// table. `name` uses minmax(0, 1fr) so it absorbs leftover horizontal space
// (and the 0 floor lets long client names truncate instead of pushing the
// column wider, which used to cause row-to-row column shift).
// On a wide screen the spare width is shared (name 2 : the rest 1 each) rather than all landing on `name`; each
// column's old fixed width is its floor, so narrower screens are unchanged and rows still resolve identical tracks.
const MG =
  '28px minmax(0, 2fr) minmax(170px, 1fr) minmax(140px, 1fr) minmax(180px, 1fr) minmax(105px, 0.6fr) minmax(100px, 0.6fr) 100px';
const insetDivider = {
  backgroundImage: `linear-gradient(${C.borderLight}, ${C.borderLight})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 1px',
  backgroundPosition: '0 bottom',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--tx-fill)',
  border: 0,
  borderRadius: 10,
  padding: '10px 14px',
  color: C.text,
  fontSize: 14,
  fontFamily: font,
  outline: 'none',
  boxSizing: 'border-box',
  height: 44,
};
const btnPrimary: React.CSSProperties = {
  background: 'var(--tx-fill-2)',
  color: 'var(--tx-text)',
  border: 'none',
  borderRadius: 10,
  height: 44,
  padding: '0 20px',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: font,
  letterSpacing: '-0.02em',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

// ─── Constants ────────────────────────────────────────────────────────────────
const DEAL_STATUSES = ['Search', 'Due Diligence', 'Offer', 'Under Contract', 'Pending', 'Closed'];
const PERIOD_TYPES = ['Initial', 'Ext I', 'Ext II', 'Ext III'];
const MEMBER_ROLES = ['Buyer', 'Co-Buyer', 'Counsel', 'Advisor', 'Other'];
// Display mapping: legacy 'Primary Owner' records show as 'Buyer', etc.
const DISPLAY_ROLE = (role: string) => {
  if (role === 'Primary Owner') return 'Buyer';
  if (role === 'Co-Signer') return 'Co-Buyer';
  if (role === 'Attorney') return 'Counsel';
  if (role === 'Agent') return 'Advisor';
  return role;
};
const PERIOD_DAYS: Record<string, number> = {
  Initial: 90,
  'Ext I': 90,
  'Ext II': 90,
  'Ext III': 90,
};
const PERIOD_OFFERS: Record<string, number> = { Initial: 6, 'Ext I': 3, 'Ext II': 3, 'Ext III': 3 };
const LISTING_STATUSES = [
  'Touring',
  'Due Diligence',
  'Offer Submitted',
  'Under Contract',
  'Pending',
  'Closed',
  'Lost',
  'Passed',
];
// LISTING_STATUS_COLOR now lives in @/constants/listingStatus — shared with
// the admin Properties panel so the two can't drift out of sync again.
const UC_LOCK_COLOR = '#c0ff02';

function UnderContractLockChip({ inline = false }: { inline?: boolean }) {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        fontFamily: font,
        color: UC_LOCK_COLOR,
        background: `${UC_LOCK_COLOR}18`,
        border: 0,
        borderRadius: 6,
        padding: '4px 8px',
        display: inline ? 'inline-flex' : 'flex',
        alignItems: 'center',
        gap: 5,
        animation: 'uc-glow-pulse 2s ease-in-out infinite',
      }}>
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      Under Contract
    </span>
  );
}
const LISTING_OFFER_STATUSES = new Set(['Offer Submitted', 'Under Contract', 'Pending', 'Closed']);
const LISTING_TO_DEAL_STATUS: Record<string, string> = {
  Touring: 'Search',
  'Due Diligence': 'Due Diligence',
  'Offer Submitted': 'Offer',
  'Under Contract': 'Under Contract',
  Pending: 'Pending',
  Closed: 'Closed',
};
// Derive a deal's effective status from its furthest-advanced active listing.
// Passed listings are ignored — they're dead and shouldn't affect deal status.
function effectiveDealStatus(deal: Deal): string {
  if (!deal.listings || deal.listings.length === 0) return deal.status;
  let best = -1;
  for (const l of deal.listings) {
    if (l.status === 'Passed' || l.status === 'Lost') continue;
    const mapped = LISTING_TO_DEAL_STATUS[l.status] || deal.status;
    const idx = DEAL_STATUSES.indexOf(mapped);
    if (idx > best) best = idx;
  }
  return best >= 0 ? DEAL_STATUSES[best] : deal.status;
}
const DEAL_OFFER_STATUSES = new Set(['Offer', 'Under Contract', 'Pending', 'Closed']);

function daysRemaining(startDateStr: string, periodType: string): number | null {
  if (!startDateStr) return null;
  const start = parseLocalDate(startDateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const totalDays = PERIOD_DAYS[periodType] || 90;
  const elapsed = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return Math.min(totalDays - Math.max(0, elapsed), totalDays);
}

function autoFee(periodType: string): number {
  return calculateAutomaticFlatFee(periodType);
}
function autoOffers(periodType: string): number {
  return PERIOD_OFFERS[periodType] || 6;
}

// Type aliases — map internal names to shared types from @/types/clientGroups
// This avoids renaming the 200+ references throughout the file.
export type Member = GroupMember;
export type Listing = DealListing;
export type Deal = ApiDeal;
export type Group = ClientGroup;

function dealEffectiveOffersUsed(deal: Deal): number {
  if (deal.listings && deal.listings.length > 0) {
    return deal.listings.reduce((s, l) => s + l.offersUsed, 0);
  }
  return deal.offersUsed;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtMoney(n: number | null): string {
  if (!n && n !== 0) return '—';
  return `$${Number(n).toLocaleString()}`;
}

function formatCurrencyInput(value: string | number): string {
  const digits = String(value).replace(/[^0-9]/g, '');
  return digits ? `$${Number(digits).toLocaleString()}` : '';
}
function fmtDate(str: string): string {
  return formatLocalDate(str);
}
function primaryOwner(members: Member[]): Member | undefined {
  return members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') || members[0];
}
function initials(name: string): string {
  return name
    .trim()
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

// ─── Sort ─────────────────────────────────────────────────────────────────────
type SortKey = 'name' | 'status' | 'expires' | 'fee';
const DEAL_STATUS_ORDER = [
  'Search',
  'Due Diligence',
  'Offer',
  'Under Contract',
  'Pending',
  'Closed',
];

function groupSortValue(g: Group, key: SortKey): number | string {
  if (key === 'name') return g.holdingName.toLowerCase();
  if (key === 'status') {
    const indices = g.deals.map((d) => DEAL_STATUS_ORDER.indexOf(effectiveDealStatus(d)));
    return indices.length ? Math.min(...indices) : 999;
  }
  if (key === 'expires') {
    const days = g.deals
      .filter((d) => effectiveDealStatus(d) !== 'Closed')
      .map((d) => daysRemaining(d.engagementStart, d.periodType))
      .filter((d): d is number => d !== null);
    return days.length ? Math.min(...days) : 999999;
  }
  if (key === 'fee') {
    return calculateRevenueForDeals(g.deals);
  }
  return 0;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function ClientAvatar({
  name,
  size = 32,
  photoUrl,
}: {
  name: string;
  size?: number;
  photoUrl?: string;
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'var(--tx-fill-2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.32,
        fontWeight: 600,
        color: 'var(--tx-text)',
        fontFamily: font,
        flexShrink: 0,
        overflow: 'hidden',
      }}>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        initials(name)
      )}
    </div>
  );
}

function ListingStatusDot({ status, showTooltip }: { status: string; showTooltip?: boolean }) {
  const color = LISTING_STATUS_COLOR[status] || '#8B8680';
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={showTooltip ? () => setHover(true) : undefined}
      onMouseLeave={showTooltip ? () => setHover(false) : undefined}>
      <div
        style={{
          // a listing's stage as a square tag (Tradehaus .tx-pill), the stage colour tinting the fill and inking the letter
          width: 22,
          height: 22,
          borderRadius: '6px',
          background: `${color}26`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 600,
          color,
          fontFamily: font,
          flexShrink: 0,
          lineHeight: '24px',
          textAlign: 'center',
          userSelect: 'none',
          cursor: showTooltip ? 'default' : undefined,
        }}>
        {status[0]}
      </div>
      {hover && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--tx-glass)',
            boxShadow: 'var(--tx-shadow-sm)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            color: 'var(--tx-text)',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: font,
            padding: '5px 10px',
            borderRadius: '9px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 9999,
          }}>
          {status}
        </div>
      )}
    </div>
  );
}

function ExpiryIndicator({ days }: { days: number | null }) {
  if (days === null) return <span style={{ color: C.subtle, fontSize: 13 }}>—</span>;

  let color: string;
  let pulse = false;
  if (days > 60) {
    color = L.low;
  } else if (days > 30) {
    color = L.moderate;
  } else if (days > 14) {
    color = L.urgent;
    pulse = true;
  } else if (days >= 0) {
    color = L.critical;
    pulse = true;
  } else {
    color = L.critical;
    pulse = true;
  }

  const label = days < 0 ? 'Expired' : `${days}d`;

  return (
    <span
      style={{
        display: 'inline-block',
        width: 'fit-content',
        background: `${color}1f`,
        color,
        borderRadius: 6,
        padding: '4px 8px',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: font,
        whiteSpace: 'nowrap',
        animation: pulse ? 'pulse-fill 1.6s ease-in-out infinite' : 'none',
      }}>
      {label}
    </span>
  );
}

function MemberRoleBadge({ role }: { role: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        background: 'var(--tx-fill-2)',
        color: 'var(--tx-muted)',
        border: 'none',
        borderRadius: 8,
        height: 30,
        padding: '0 10px',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: font,
        whiteSpace: 'nowrap',
      }}>
      {DISPLAY_ROLE(role)}
    </span>
  );
}

function InvitePendingBadge() {
  return (
    <span
      style={{
        background: 'rgba(240,165,0,0.12)',
        color: C.amber,
        border: 0,
        borderRadius: 6,
        padding: '4px 8px',
        fontSize: 10,
        fontWeight: 600,
        fontFamily: font,
        whiteSpace: 'nowrap',
      }}>
      Invite Pending
    </span>
  );
}

function ResendInviteButton({ email }: { email: string }) {
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const handleResend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (sending || sent) return;
    setSending(true);
    try {
      await resendInviteByEmail(email);
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch {
      // silently ignore — advisor can retry
    } finally {
      setSending(false);
    }
  };

  return (
    <button
      onClick={handleResend}
      disabled={sending}
      title={sent ? 'Invite resent!' : 'Resend invite'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        height: 30,
        padding: '0 10px',
        borderRadius: 8,
        background: sent ? 'rgba(192,255,2,0.1)' : 'var(--tx-fill)',
        border: 0,
        color: sent ? 'var(--tx-lime-ink)' : C.muted,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: font,
        cursor: sending ? 'wait' : 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}>
      {sent ? 'Sent!' : sending ? '…' : 'Resend'}
    </button>
  );
}

// ─── Status colors (also used by kanban) ──────────────────────────────────────
const STATUS_COLOR: Record<string, string> = {
  Search: '#bbb7af',
  'Due Diligence': L.critical,
  Offer: L.urgent,
  'Under Contract': L.moderate,
  Pending: L.low,
  Closed: '#C0FF02',
};

// ─── Small group-level expiry donut (parent row only) ────────────────────────
function GroupExpiryDonut({ deals }: { deals: Deal[] }) {
  const active = deals.filter((d) => effectiveDealStatus(d) !== 'Closed');
  const buckets = [
    { color: L.critical, test: (r: number) => r <= 14 },
    { color: L.urgent, test: (r: number) => r > 14 && r <= 30 },
    { color: L.moderate, test: (r: number) => r > 30 && r <= 60 },
    { color: L.low, test: (r: number) => r > 60 },
  ]
    .map((b) => ({
      ...b,
      value: active.filter((d) => {
        const r = daysRemaining(d.engagementStart, d.periodType);
        return r !== null && b.test(r);
      }).length,
    }))
    .filter((b) => b.value > 0);

  const total = buckets.reduce((s, b) => s + b.value, 0);
  if (total === 0)
    return <span style={{ color: C.subtle, fontSize: 12, fontFamily: font }}>—</span>;

  // the client's agreements as a hairline strip, soonest-expiring first: the Expirations KPI's language at row size
  return (
    <div
      title={buckets.map((b) => `${b.value}`).join(' · ')}
      style={{ display: 'flex', gap: 2, width: 56, height: 4, borderRadius: 2, overflow: 'hidden' }}>
      {buckets.map((b, i) => (
        <div key={i} style={{ flex: b.value, background: b.color }} />
      ))}
    </div>
  );
}

// ─── Stat cards ───────────────────────────────────────────────────────────────
function StatCards({ groups }: { groups: Group[] }) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const allDeals = groups.flatMap((g) => g.deals);
  const allListings = allDeals.flatMap((d) => d.listings ?? []);
  const active = allDeals.filter((d) => effectiveDealStatus(d) !== 'Closed').length;
  const closedDeals = allDeals.filter(
    (d) => effectiveDealStatus(d) === 'Closed' && d.engagementStart,
  );
  // days from engagement to close, per closed deal
  const closeDays = closedDeals.map((d) => {
          // Prefer the explicit closedDate stamp (set by backend on status → Closed).
          // For deals closed before that field existed, derive the close time from the
          // latest updatedAt among the deal's Closed listings — that timestamp reflects
          // when the listing was actually marked closed, unlike d.updatedAt which only
          // changes when the deal record itself is written. Only fall back to d.updatedAt
          // if the deal has no listings at all (legacy deal-level status close).
          let closeTs: number;
          if (d.closedDate) {
            closeTs = d.closedDate * 1000;
          } else if (d.listings && d.listings.length > 0) {
            const closedListingTs = d.listings
              .filter((l) => l.status === 'Closed')
              .map((l) => l.updatedAt);
            closeTs =
              closedListingTs.length > 0 ? Math.max(...closedListingTs) * 1000 : d.updatedAt * 1000;
          } else {
            closeTs = d.updatedAt * 1000;
          }
    return Math.ceil((closeTs - new Date(d.engagementStart).getTime()) / (1000 * 60 * 60 * 24));
  });
  const avgDaysToClose = closeDays.length
    ? Math.round(closeDays.reduce((sum, n) => sum + n, 0) / closeDays.length)
    : null;
  // The Tradehaus widget row (a phone takes it as a two-column grid, the pipeline and fees at full width). Each card is a sheet with its label, a light figure and, where the data has a
  // shape, a hairline visual: segmented strips for expirations and fees, the pipeline as thin bars
  const pipelineCounts = DEAL_STATUS_ORDER.map((status) => ({
    status,
    count: allListings.filter(
      (l) => l.status !== 'Passed' && l.status !== 'Lost' && (LISTING_TO_DEAL_STATUS[l.status] || 'Search') === status,
    ).length,
  }));
  const pipelineMax = Math.max(1, ...pipelineCounts.map((p) => p.count));
  const activeDeals = allDeals.filter((d) => effectiveDealStatus(d) !== 'Closed');
  const expiryBuckets = [
    { label: 'Expired', color: L.critical, test: (d: number) => d < 0 },
    { label: '≤ 14 days', color: L.critical, test: (d: number) => d >= 0 && d <= 14 },
    { label: '15–30 days', color: L.urgent, test: (d: number) => d > 14 && d <= 30 },
    { label: '31–60 days', color: L.moderate, test: (d: number) => d > 30 && d <= 60 },
    { label: '> 60 days', color: L.low, test: (d: number) => d > 60 },
  ].map((b) => ({
    label: b.label,
    color: b.color,
    value: activeDeals.filter((d) => {
      const r = daysRemaining(d.engagementStart, d.periodType);
      return r !== null && b.test(r);
    }).length,
  }));
  const dealFee = (d: Deal) => calculateRevenueForDeal(d) ?? 0;
  const feeSlices = [
    ...['Search', 'Due Diligence', 'Offer', 'Under Contract', 'Pending'].map((stage) => ({
      label: stage === 'Search' ? 'Search/Touring' : stage,
      color: STATUS_COLOR[stage] || C.muted,
      value: activeDeals.filter((d) => effectiveDealStatus(d) === stage).reduce((sum, d) => sum + dealFee(d), 0),
    })),
    {
      label: 'Closed',
      color: '#C0FF02',
      value: allDeals.filter((d) => effectiveDealStatus(d) === 'Closed').reduce((sum, d) => sum + dealFee(d), 0),
    },
  ];
  const feeTotal = feeSlices.reduce((sum, f) => sum + f.value, 0);
  const shortStage: Record<string, string> = {
    Search: 'Search',
    'Due Diligence': 'Diligence',
    Offer: 'Offer',
    'Under Contract': 'Contract',
    Pending: 'Pending',
    Closed: 'Closed',
  };

  const kpiCard: React.CSSProperties = {
    background: C.card,
    backgroundImage: 'var(--tx-surface-light)',
    boxShadow: 'var(--tx-shadow)',
    borderRadius: '24px',
    padding: 20,
    minHeight: 150,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  };
  // value first, its label beneath (Tradehaus .tx-vz-card)
  const kpiLabel: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', fontFamily: font, marginTop: -8 };
  const figure = (value: React.ReactNode, unit?: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontFamily: font }}>
      <span
        style={{
          fontSize: 40,
          fontWeight: 200,
          letterSpacing: '-0.05em',
          lineHeight: 1,
          color: 'var(--tx-text)',
          fontVariantNumeric: 'proportional-nums',
        }}>
        {value}
      </span>
      {unit && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>{unit}</span>}
    </div>
  );
  const caption = (left: string, right: string) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', fontFamily: font }}>
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
  // a segmented hairline strip: each part's share of the whole in its colour, a quiet track when empty
  const strip = (parts: { label: string; color: string; value: number }[], format: (v: number) => string) => {
    const total = parts.reduce((sum, part) => sum + part.value, 0);
    return (
      <div style={{ display: 'flex', gap: 2, height: 4, borderRadius: 2, overflow: 'hidden', background: total ? 'transparent' : 'var(--tx-fill-2)' }}>
        {parts
          .filter((part) => part.value > 0)
          .map((part) => (
            <Tooltip key={part.label} title={`${part.label} · ${format(part.value)}`} placement="top">
              <div style={{ flex: part.value, background: part.color, cursor: 'help' }} />
            </Tooltip>
          ))}
      </div>
    );
  };

  // a dot per client: filled while they have a deal in progress, faint otherwise (Tradehaus Directory tile)
  const clientDots = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {groups.map((g) => {
        const isActive = g.deals.some((d) => effectiveDealStatus(d) !== 'Closed');
        return (
          <span
            key={g.id}
            title={g.holdingName}
            style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? 'var(--tx-text)' : 'var(--tx-fill-3)' }}
          />
        );
      })}
    </div>
  );
  const activeClientCount = groups.filter((g) => g.deals.some((d) => effectiveDealStatus(d) !== 'Closed')).length;
  // a hairline per value, height by value, on a baseline (Tradehaus Punch List tile)
  const ticks = (values: number[], empty: string) => {
    const max = Math.max(1, ...values);
    return values.length ? (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 28, borderBottom: '1px solid var(--tx-line)' }}>
        {values.map((v, i) => (
          <span key={i} style={{ width: 2, height: `${Math.max(12, (v / max) * 100)}%`, borderRadius: '1px 1px 0 0', background: 'var(--tx-muted)' }} />
        ))}
      </div>
    ) : (
      // empty: just the quiet caption, no baseline (a lone hairline at the tile's foot read as a stray shadow)
      <div style={{ height: 28, display: 'flex', alignItems: 'flex-end', fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)', fontFamily: font }}>
        {empty}
      </div>
    );
  };
  const dealAges = activeDeals
    .map((d) => (d.engagementStart ? Math.max(0, Math.floor((Date.now() - new Date(d.engagementStart).getTime()) / 86400000)) : 0))
    .sort((a, b) => a - b);

  return (
    <div style={{ marginBottom: isMobile ? 10 : 20 }}>
      <div
        style={
          isMobile
            ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gridAutoFlow: 'dense', gap: 10 }
            : { display: 'flex', gap: 20 }
        }>
        <div style={{ ...kpiCard, flex: '0.8 1 110px' }}>
          {figure(groups.length)}
          <div style={kpiLabel}>Clients</div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clientDots}
            {caption(`${activeClientCount} active`, '')}
          </div>
        </div>
        <div style={{ ...kpiCard, flex: '0.8 1 110px' }}>
          {figure(active)}
          <div style={kpiLabel}>Active deals</div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ticks(dealAges, 'None open')}
            {caption('Newest', 'Oldest')}
          </div>
        </div>
        <div style={{ ...kpiCard, flex: '1.4 1 180px' }}>
          {figure(activeDeals.length, 'active')}
          <div style={kpiLabel}>Expirations</div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {strip(expiryBuckets, (v) => `${v} ${v === 1 ? 'deal' : 'deals'}`)}
            {caption('Expiring soonest', '> 60 days')}
          </div>
        </div>
        <div style={{ ...kpiCard, flex: '2.4 1 320px', ...(isMobile && { gridColumn: '1 / -1' }) }}>
          {figure(pipelineCounts.reduce((sum, p) => sum + p.count, 0))}
          <div style={kpiLabel}>Pipeline</div>
          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
            {pipelineCounts.map(({ status, count }) => (
              <div key={status} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    fontSize: 17,
                    fontWeight: 300,
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                    color: count ? 'var(--tx-text)' : 'var(--tx-faint)',
                    fontFamily: font,
                  }}>
                  {count}
                </span>
                <div style={{ height: 40, display: 'flex', alignItems: 'flex-end' }}>
                  <div
                    style={{
                      width: 6,
                      height: count ? `${Math.max(12, (count / pipelineMax) * 100)}%` : 4,
                      borderRadius: 3,
                      background: count ? STATUS_COLOR[status] : 'var(--tx-fill-2)',
                    }}
                  />
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx-muted)', fontFamily: font, whiteSpace: 'nowrap' }}>
                  {shortStage[status] ?? status}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ ...kpiCard, flex: '0.9 1 120px' }}>
          {avgDaysToClose !== null ? figure(avgDaysToClose) : figure('—')}
          <div style={kpiLabel}>Avg. days to close</div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {ticks([...closeDays].sort((a, b) => a - b), 'No closed deals yet')}
            {closeDays.length > 0 && caption('Fastest', 'Slowest')}
          </div>
        </div>
        <div style={{ ...kpiCard, flex: '1.4 1 180px', ...(isMobile && { gridColumn: '1 / -1' }) }}>
          {figure(feeTotal >= 1000 ? `$${Math.round(feeTotal / 1000)}K` : `$${feeTotal.toLocaleString()}`)}
          <div style={kpiLabel}>Total fees</div>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {strip(feeSlices, (v) => `$${v.toLocaleString()}`)}
            {caption('In pipeline', 'Closed')}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Kanban board ─────────────────────────────────────────────────────────────
const KANBAN_STATUSES = ['Search', 'Due Diligence', 'Offer', 'Under Contract', 'Pending', 'Closed'];
const KANBAN_LABELS: Record<string, string> = { Search: 'Search/Touring' };

// Reverse map: kanban column → listing status
const KANBAN_TO_LISTING: Record<string, string> = {
  Search: 'Touring',
  'Due Diligence': 'Due Diligence',
  Offer: 'Offer Submitted',
  'Under Contract': 'Under Contract',
  Pending: 'Pending',
  Closed: 'Closed',
};

function KanbanBoard({
  groups,
  onDealStatusChange,
  onListingStatusChange,
}: {
  groups: Group[];
  onDealStatusChange: (
    groupId: string,
    dealId: string,
    fromStatus: string,
    toStatus: string,
  ) => void;
  onListingStatusChange: (
    groupId: string,
    dealId: string,
    listingId: string,
    newListingStatus: string,
  ) => void;
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [dragging, setDragging] = useState<{
    groupId: string;
    dealId: string;
    listingId: string;
    fromStatus: string;
    isDealCard: boolean;
  } | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [mobileActiveStatus, setMobileActiveStatus] = useState(KANBAN_STATUSES[0]);
  const [mobilePicker, setMobilePicker] = useState<{
    groupId: string;
    dealId: string;
    listingId: string;
    currentStatus: string;
    groupName: string;
    isDealCard: boolean;
  } | null>(null);

  // Map listing statuses → kanban columns
  const LISTING_TO_KANBAN: Record<string, string> = {
    Touring: 'Search',
    'Due Diligence': 'Due Diligence',
    'Offer Submitted': 'Offer',
    'Under Contract': 'Under Contract',
    Pending: 'Pending',
    Closed: 'Closed',
  };

  const getListingCards = (kanbanStatus: string) =>
    groups.flatMap((g) => {
      const owner = primaryOwner(g.members);
      return g.deals.flatMap((d) => {
        const listings = d.listings ?? [];
        if (listings.length === 0) {
          // Deal has no listings — show it as a deal-level card at its own status column
          if (d.status === kanbanStatus) {
            return [
              { group: g, deal: d, listing: null as Listing | null, owner, isDealCard: true },
            ];
          }
          return [];
        }
        return listings
          .filter((l) => LISTING_TO_KANBAN[l.status] === kanbanStatus)
          .map((l) => ({
            group: g,
            deal: d,
            listing: l as Listing | null,
            owner,
            isDealCard: false,
          }));
      });
    });

  if (isMobile) {
    const activeCards = getListingCards(mobileActiveStatus);
    const activeColor = STATUS_COLOR[mobileActiveStatus];
    return (
      <div style={{ background: C.card, borderRadius: 25, padding: 20, marginBottom: 10 }}>
        {/* Status chip switcher */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            paddingBottom: 14,
            marginBottom: 14,
            scrollbarWidth: 'none',
          }}>
          {KANBAN_STATUSES.map((s) => {
            const count = getListingCards(s).length;
            const color = STATUS_COLOR[s];
            const isActive = mobileActiveStatus === s;
            return (
              <button
                key={s}
                onClick={() => setMobileActiveStatus(s)}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: 0,
                  background: isActive ? `${color}22` : 'var(--tx-fill)',
                  color: isActive ? color : C.muted,
                  fontSize: 11,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  fontFamily: font,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}>
                {KANBAN_LABELS[s] || s}
                <span style={{ fontSize: 10, fontWeight: 600, color: isActive ? color : C.subtle }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Cards for active status */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 60 }}>
          {activeCards.length === 0 ? (
            <div
              style={{
                padding: 20,
                borderRadius: 10,
                border: 'none',
                background: 'var(--tx-fill)',
                textAlign: 'center',
              }}>
              <span style={{ fontSize: 12, color: C.subtle, fontFamily: font }}>
                No listings in this stage
              </span>
            </div>
          ) : (
            activeCards.map(({ group, deal, listing, owner, isDealCard }) => (
              <div
                key={isDealCard ? `deal-${deal.id}` : listing?.id ?? deal.id}
                onClick={() =>
                  setMobilePicker({
                    groupId: group.id,
                    dealId: deal.id,
                    listingId: listing?.id ?? '',
                    currentStatus: mobileActiveStatus,
                    groupName: (listing?.address || deal.name || group.holdingName).split(',')[0],
                    isDealCard: isDealCard ?? false,
                  })
                }
                style={{
                  background: 'rgba(var(--tx-ink-rgb), 0.05)',
                  borderRadius: '12px',
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}>
                <ClientAvatar name={owner?.name ?? ''} size={28} photoUrl={owner?.photoUrl} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.text,
                      fontFamily: font,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {(listing?.address || deal.name || group.holdingName).split(',')[0]}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: C.muted,
                      fontFamily: font,
                      marginTop: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {group.holdingName}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  <span
                    style={{ fontSize: 11, color: activeColor, fontFamily: font, fontWeight: 600 }}>
                    Move
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={activeColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Move status overlay dialog */}
        {mobilePicker && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1200,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              padding: '0 10px 20vh',
            }}>
            <div
              onClick={() => setMobilePicker(null)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }}
            />
            <div
              style={{
                position: 'relative',
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                borderRadius: '24px',
                padding: 20,
                width: '100%',
              }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.muted,
                  fontFamily: font,
                  marginBottom: 16,
                }}>
                Move {mobilePicker.groupName}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {KANBAN_STATUSES.map((s) => {
                  const color = STATUS_COLOR[s];
                  const isCurrent = s === mobilePicker.currentStatus;
                  return (
                    <button
                      key={s}
                      onClick={() => {
                        if (!isCurrent) {
                          if (mobilePicker.isDealCard) {
                            onDealStatusChange(
                              mobilePicker.groupId,
                              mobilePicker.dealId,
                              mobilePicker.currentStatus,
                              s,
                            );
                          } else {
                            const newListingStatus = KANBAN_TO_LISTING[s];
                            if (newListingStatus)
                              onListingStatusChange(
                                mobilePicker.groupId,
                                mobilePicker.dealId,
                                mobilePicker.listingId,
                                newListingStatus,
                              );
                          }
                        }
                        setMobilePicker(null);
                        if (!isCurrent) setMobileActiveStatus(s);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '16px 20px',
                        borderRadius: 20,
                        border: 'none',
                        background: isCurrent ? `${color}18` : C.card,
                        cursor: isCurrent ? 'default' : 'pointer',
                        textAlign: 'left',
                        width: '100%',
                      }}>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: color,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: isCurrent ? color : C.text,
                          fontFamily: font,
                          flex: 1,
                        }}>
                        {KANBAN_LABELS[s] || s}
                      </span>
                      {isCurrent && (
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color,
                            fontFamily: font,
                          }}>
                          Current
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'visible', marginBottom: 20 }}>
      <div
        style={{
          display: 'flex',
          gap: 12,
          background: C.card,
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          borderRadius: '24px',
          padding: 20,
        }}>
        {KANBAN_STATUSES.map((status) => {
          const color = STATUS_COLOR[status];
          const cards = getListingCards(status);
          const isOver = dragOver === status && dragging?.fromStatus !== status;

          return (
            <div
              key={status}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(status);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging && dragging.fromStatus !== status) {
                  if (dragging.isDealCard) {
                    onDealStatusChange(
                      dragging.groupId,
                      dragging.dealId,
                      dragging.fromStatus,
                      status,
                    );
                  } else {
                    const newListingStatus = KANBAN_TO_LISTING[status];
                    if (newListingStatus) {
                      onListingStatusChange(
                        dragging.groupId,
                        dragging.dealId,
                        dragging.listingId,
                        newListingStatus,
                      );
                    }
                  }
                }
                setDragging(null);
                setDragOver(null);
              }}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                minWidth: 0,
                // a stage as a neutral lane (a tile's tonal fill); its colour lives in the header's hairline and the
                // outline while a card is dragged over it
                padding: 12,
                borderRadius: '16px',
                background: 'rgba(var(--tx-ink-rgb), 0.035)',
                outline: isOver ? `2px solid ${color}` : '2px solid transparent',
                transition: 'outline-color 0.15s',
              }}>
              {/* header (Tradehaus .tx-colhead.fig): the stage's hairline and name, its light count on the right */}
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8, padding: '2px 2px 4px' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                    fontSize: 14,
                    fontWeight: 400,
                    letterSpacing: '-0.015em',
                    color: 'var(--tx-text)',
                    fontFamily: font,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  <span style={{ width: 2, height: 12, borderRadius: 1, background: color, flexShrink: 0 }} />
                  {KANBAN_LABELS[status] || status}
                </span>
                <span
                  style={{
                    fontSize: 26,
                    fontWeight: 200,
                    letterSpacing: '-0.05em',
                    lineHeight: 1,
                    color: cards.length ? 'var(--tx-text)' : 'var(--tx-faint)',
                    fontFamily: font,
                  }}>
                  {cards.length}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minHeight: 40 }}>
                {cards.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 52,
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'var(--tx-faint)',
                      fontFamily: font,
                    }}>
                    {isOver ? 'Drop here' : 'No listings'}
                  </div>
                ) : (
                  cards.map(({ group, deal, listing, owner, isDealCard }) => {
                    const cardKey = isDealCard ? `deal-${deal.id}` : listing?.id ?? deal.id;
                    const isDraggingThis = isDealCard
                      ? dragging?.dealId === deal.id && dragging?.isDealCard
                      : listing?.id !== undefined && dragging?.listingId === listing.id;
                    const cardTitle = (listing?.address || deal.name || group.holdingName).split(
                      ',',
                    )[0];
                    return (
                      <div
                        key={cardKey}
                        draggable
                        onDragStart={() =>
                          setDragging({
                            groupId: group.id,
                            dealId: deal.id,
                            listingId: listing?.id ?? '',
                            fromStatus: status,
                            isDealCard: isDealCard ?? false,
                          })
                        }
                        onDragEnd={() => {
                          setDragging(null);
                          setDragOver(null);
                        }}
                        style={{
                          background: 'rgba(var(--tx-ink-rgb), 0.05)',
                          borderRadius: '9px',
                          padding: '9px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'grab',
                          opacity: isDraggingThis ? 0.35 : 1,
                          transition: 'opacity 0.15s',
                          userSelect: 'none',
                        }}>
                        <ClientAvatar
                          name={owner?.name ?? ''}
                          size={24}
                          photoUrl={owner?.photoUrl}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: C.text,
                              fontFamily: font,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                            {cardTitle}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              color: C.muted,
                              fontFamily: font,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              marginTop: 2,
                            }}>
                            {group.holdingName}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CustomSelect({
  value,
  options,
  onChange,
  triggerStyle,
  onClick,
  showChevron = true,
}: {
  value: string | number;
  // color: the option's status colour, drawn as the app's 2px tick before its label
  options: { value: string | number; label: string; color?: string }[];
  onChange: (v: string) => void;
  triggerStyle?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  showChevron?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0, minWidth: 0 });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(t) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(t)
      )
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    onClick?.(e);
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      const estHeight = options.length * 30 + 12;
      const spaceBelow = window.innerHeight - r.bottom;
      const top = spaceBelow < estHeight ? r.top - estHeight - 4 : r.bottom + 4;
      // Right-align to trigger's right edge so it never bleeds off the right side on mobile
      setPos({ top, right: window.innerWidth - r.right, minWidth: r.width });
    }
    setOpen((o) => !o);
  };

  const selected = options.find((o) => String(o.value) === String(value));
  const label = selected?.label ?? String(value);
  return (
    <div
      ref={triggerRef}
      style={{ display: 'inline-flex', alignItems: 'center' }}
      onClick={(e) => e.stopPropagation()}>
      <div
        onClick={handleToggle}
        style={{
          borderRadius: 6,
          padding: '4px 10px',
          fontSize: 11,
          fontWeight: 600,
          fontFamily: font,
          cursor: 'pointer',
          userSelect: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          ...triggerStyle,
        }}>
        {selected?.color && (
          <span style={{ width: 2, height: 12, borderRadius: 1, background: selected.color, flexShrink: 0, marginRight: 2 }} />
        )}
        {label}
        {showChevron && (
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ opacity: 0.6, flexShrink: 0 }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        )}
      </div>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: pos.top,
              right: pos.right,
              zIndex: 9999,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '12px',
              padding: '4px 6px',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              minWidth: pos.minWidth,
              boxShadow: 'var(--tx-shadow)',
              whiteSpace: 'nowrap',
            }}>
            {options.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <div
                  key={opt.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(String(opt.value));
                    setOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                    borderRadius: 6,
                    color: isSelected ? 'var(--tx-text)' : C.text,
                    background: isSelected ? 'var(--tx-fill-2)' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLDivElement).style.background = '#bbb7af22';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                  }}>
                  {opt.color && (
                    <span style={{ width: 2, height: 12, borderRadius: 1, background: opt.color, flexShrink: 0 }} />
                  )}
                  {opt.label}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

type AdvisorOption = { userId: string; name?: string; email?: string };

function CoAdvisorSelect({
  value,
  advisors,
  primaryAdvisorId,
  onChange,
  editable = true,
}: {
  value: string[];
  advisors: AdvisorOption[];
  primaryAdvisorId: string;
  onChange: (ids: string[]) => void;
  /** When false, render existing chips read-only and hide the add/remove
   *  affordances. Use for non-primary-advisor viewers — the BE rejects
   *  coAdvisorIds writes from anyone but the primary advisor / admin. */
  editable?: boolean;
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingRemove, setPendingRemove] = useState<AdvisorOption | null>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const dialogPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pendingRemove) return;
    dialogPanelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPendingRemove(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pendingRemove]);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const selected = useMemo(
    () =>
      value
        .map((id) => advisors.find((a) => a.userId === id))
        .filter((a): a is AdvisorOption => !!a),
    [advisors, value],
  );

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = advisors.filter((a) => a.userId !== primaryAdvisorId && !value.includes(a.userId));
    if (!q) return pool.slice(0, 8);
    return pool
      .filter(
        (a) =>
          (a.name ?? '').toLowerCase().includes(q) || (a.email ?? '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [advisors, primaryAdvisorId, value, search]);

  useEffect(() => {
    if (isSearching && inputWrapperRef.current) {
      const r = inputWrapperRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    } else {
      setDropdownPos(null);
    }
  }, [isSearching]);

  const addAdvisor = (userId: string) => {
    onChange([...value, userId]);
    setSearch('');
    setIsSearching(false);
  };

  const confirmRemove = () => {
    if (!pendingRemove) return;
    onChange(value.filter((id) => id !== pendingRemove.userId));
    setPendingRemove(null);
  };

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minWidth: 0 }}
      onClick={(e) => e.stopPropagation()}>
      {selected.map((a) => (
        <span
          key={a.userId}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'var(--tx-fill-2)',
            border: 'none',
            borderRadius: 6,
            padding: '4px 6px 4px 10px',
            fontSize: 11,
            fontWeight: 600,
            fontFamily: font,
            color: C.muted,
            maxWidth: '100%',
            whiteSpace: 'nowrap',
          }}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 110,
            }}>
            {a.name || a.email || a.userId}
          </span>
          {editable && (
            <button
              onClick={() => setPendingRemove(a)}
              aria-label="Remove co-advisor"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: C.subtle,
                padding: '0 2px',
                display: 'inline-flex',
                alignItems: 'center',
                lineHeight: 0,
                WebkitAppearance: 'none',
                appearance: 'none',
              }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </span>
      ))}
      {editable &&
        (isSearching ? (
          <div ref={inputWrapperRef} style={{ position: 'relative', flex: 1, minWidth: 120 }}>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsSearching(false);
                  setSearch('');
                }
              }}
              placeholder="Search advisors…"
              style={{
                width: '100%',
                height: 26,
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: 6,
                padding: '0 8px',
                color: C.text,
                fontSize: 11,
                fontFamily: font,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = C.muted;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
                setTimeout(() => {
                  setIsSearching(false);
                  setSearch('');
                }, 150);
              }}
            />
            {dropdownPos &&
              createPortal(
                <div
                  onMouseDown={(e) => e.preventDefault()}
                  style={{
                    position: 'fixed',
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: Math.max(dropdownPos.width, 220),
                    zIndex: 9999,
                    background: 'var(--tx-glass-panel)',
                    backgroundImage: 'var(--tx-surface-light)',
                    borderRadius: '12px',
                    padding: '4px 6px',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                    maxHeight: 240,
                    overflowY: 'auto',
                    boxShadow: 'var(--tx-shadow)',
                  }}>
                  {matches.length === 0 ? (
                    <div
                      style={{
                        padding: '8px 10px',
                        fontSize: 11,
                        color: C.subtle,
                        fontFamily: font,
                      }}>
                      No matching advisors
                    </div>
                  ) : (
                    matches.map((a) => (
                      <div
                        key={a.userId}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addAdvisor(a.userId);
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '6px 10px',
                          borderRadius: 6,
                          cursor: 'pointer',
                          color: C.text,
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.background =
                            'rgba(255,255,255,0.06)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                        }}>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            fontFamily: font,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {a.name || a.email || a.userId}
                        </span>
                        {a.name && a.email && (
                          <span
                            style={{
                              fontSize: 10,
                              color: C.subtle,
                              fontFamily: font,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                            {a.email}
                          </span>
                        )}
                      </div>
                    ))
                  )}
                </div>,
                document.body,
              )}
          </div>
        ) : (
          <button
            onClick={() => setIsSearching(true)}
            aria-label="Add co-advisor"
            style={{
              color: C.subtle,
              border: 'none',
                background: 'var(--tx-fill)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 600,
              fontFamily: font,
              cursor: 'pointer',
            }}>
            {selected.length === 0 ? (
              <>
                <span style={{ color: '#c0ff02' }}>+</span> {isMobile ? 'Add Co-advisor' : 'Add'}
              </>
            ) : (
              <span style={{ color: '#c0ff02' }}>+</span>
            )}
          </button>
        ))}
      {pendingRemove &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9998,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--tx-backdrop)',
              backdropFilter: 'blur(4px)',
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setPendingRemove(null);
            }}>
            <div
              ref={dialogPanelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="remove-coadvisor-title"
              tabIndex={-1}
              style={{
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                outline: 'none',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderRadius: '24px',
                padding: 25,
                width: 360,
                maxWidth: 'calc(100vw - 40px)',
              }}>
              <div
                id="remove-coadvisor-title"
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: C.text,
                  fontFamily: font,
                  marginBottom: 8,
                }}>
                Remove co-advisor?
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: C.muted,
                  fontFamily: font,
                  marginBottom: 20,
                  lineHeight: 1.5,
                }}>
                {pendingRemove.name || pendingRemove.email || pendingRemove.userId} will lose
                visibility into this deal.
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  onClick={() => setPendingRemove(null)}
                  style={{
                    background: 'var(--tx-fill)',
                    color: C.muted,
                    border: 0,
                    borderRadius: 10,
                    padding: '0 16px',
                    height: 40,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={confirmRemove}
                  style={{
                    background: L.critical,
                    color: 'var(--tx-text)',
                    border: 'none',
                    borderRadius: 10,
                    padding: '0 16px',
                    height: 40,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Remove
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

function DealRow({
  deal,
  onUpdate,
  onEdit,
  advisors,
  primaryAdvisorId,
  members = [],
  lockedPropertyIds = [],
  groupIsOpen = false,
}: {
  deal: Deal;
  onUpdate?: (update: Partial<Deal>) => void;
  onEdit?: () => void;
  advisors: AdvisorOption[];
  primaryAdvisorId: string;
  /** Members of the deal's ClientGroup — used to pick a buyer on Close Deal. */
  members?: GroupMember[];
  lockedPropertyIds?: string[];
  groupIsOpen?: boolean;
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(groupIsOpen);
  }, [groupIsOpen]);
  const days = daysRemaining(deal.engagementStart, deal.periodType);
  const fee = calculateRevenueForDeal(deal);
  const maxOffers = autoOffers(deal.periodType);
  const lockedSet = useMemo(() => new Set(lockedPropertyIds), [lockedPropertyIds]);

  const { beginProcessingIndicator } = useUploaderContext();
  // Listing mutation hooks — used directly to avoid passing listing arrays through onUpdate
  const createListingMutDR = useCreateListing();
  const updateListingMutDR = useUpdateListing();
  const deleteListingMutDR = useDeleteListing();
  const { canAccessAdmin } = useUserRole();
  const { user } = useAuthContext();
  const isOwner = user?.id === primaryAdvisorId;
  const isCoAdvisor = (deal.coAdvisorIds ?? []).includes(user?.id ?? '');
  // The deal's own advisors keep edit rights on terminal statuses so they can
  // advance UC → Pending → Closed. Competing advisors are blocked separately
  // via isLocked (set by BE when another group has the property Under Contract).
  const canEditTerminal = isOwner || isCoAdvisor || canAccessAdmin;

  const listings = deal.listings ?? [];
  const [pendingTerminal, setPendingTerminal] = useState<{
    listingId: string;
    newStatus: string;
  } | null>(null);
  const [acceptedPrice, setAcceptedPrice] = useState('');

  // Close-Deal-specific selections. Only meaningful while the terminal modal
  // is open with newStatus === 'Closed'. Reset every time the modal opens so
  // stale selections from a prior listing don't leak in.
  const isCloseDeal = pendingTerminal?.newStatus === 'Closed';
  const closeListing =
    isCloseDeal && pendingTerminal
      ? listings.find((l) => l.id === pendingTerminal.listingId)
      : undefined;
  const { documentRecords: closeDocRecordsRaw } = useDocumentRecords(
    isCloseDeal ? closeListing?.propertyId ?? '' : '',
  );
  // Mirror the filter from PropertiesPageNew — only docs with an actual upload
  // are transferable. Pending/unfilled rows would 404 the cascade.
  const closeDocs = (closeDocRecordsRaw || []).filter((r: any) => r.documentId);
  const [closeDealBuyerId, setCloseDealBuyerId] = useState<string>('');
  const [closeDealDocIds, setCloseDealDocIds] = useState<Set<string>>(new Set());
  // In-flight guard for the Close Deal confirm. Without this, a double-click
  // on the CTA fires two status mutations + two transfers + two audit rows.
  const [terminalSubmitting, setTerminalSubmitting] = useState(false);
  const [terminalDialogShiftX, setTerminalDialogShiftX] = useState(0);
  useEffect(() => {
    if (!pendingTerminal) return;
    const measure = () => {
      const main = document.getElementById('dashboard-main-content');
      if (main) {
        const r = main.getBoundingClientRect();
        setTerminalDialogShiftX(r.left + r.width / 2 - window.innerWidth / 2);
      }
    };
    measure();
    const sidebar = document.getElementById('dashboard-left-sidebar');
    const main = document.getElementById('dashboard-main-content');
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      if (sidebar) ro.observe(sidebar);
      if (main) ro.observe(main);
    }
    window.addEventListener('resize', measure);
    const interval = window.setInterval(measure, 50);
    const timeout = window.setTimeout(() => window.clearInterval(interval), 400);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [pendingTerminal]);

  // Concatenated doc-id signature so this effect re-runs when the doc set
  // actually changes (added/removed/renamed), not just when the count
  // happens to be the same between two listings.
  const closeDocIdsKey = closeDocs.map((r: any) => r.id).join(',');
  // Same idea for members — picking a different listing on a different deal
  // shouldn't leak the prior deal's pre-selected buyer.
  const memberIdsKey = members.map((m) => m.id).join(',');
  useEffect(() => {
    if (!isCloseDeal) return;
    // Prefer the first member who has actually signed up (has a userId). If
    // nobody is signed up we still pre-select the first member so the dropdown
    // isn't empty — the submit handler short-circuits to the skip toast.
    const signedUp = members.find((m) => m.userId);
    const fallback = members[0];
    setCloseDealBuyerId(signedUp?.id ?? fallback?.id ?? '');
    setCloseDealDocIds(new Set(closeDocs.map((r: any) => r.id)));
  }, [
    isCloseDeal,
    pendingTerminal?.listingId,
    closeListing?.propertyId,
    memberIdsKey,
    closeDocIdsKey,
  ]);
  const [pendingOffer, setPendingOffer] = useState<{ listingId: string; newStatus: string } | null>(
    null,
  );
  const [showAddListing, setShowAddListing] = useState(false);
  const [addListingTab, setAddListingTab] = useState<'existing' | 'new'>('new');
  const [listingSearch, setListingSearch] = useState('');
  const placesLibDR = useMapsLibrary('places');
  const mapsLibDR = useMapsLibrary('maps');
  const [geocoderDR, setGeocoderDR] = useState<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (mapsLibDR) setGeocoderDR(new google.maps.Geocoder());
  }, [mapsLibDR]);

  const [newAddr, setNewAddr] = useState('');
  const [newAddrOptions, setNewAddrOptions] = useState<google.maps.places.AutocompletePrediction[]>(
    [],
  );
  const [newAddrSelected, setNewAddrSelected] =
    useState<google.maps.places.AutocompletePrediction | null>(null);
  const [newAddrParsed, setNewAddrParsed] = useState<{
    address: string;
    city: string;
    state: string;
    zipCode: string;
    latitude: number;
    longitude: number;
  } | null>(null);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newReportSource, setNewReportSource] = useState<'seller' | 'buyer'>('seller');
  const [newSubmitting, setNewSubmitting] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);
  const { properties: adminProps } = useAdminProperties();
  const existingPropertyIds = new Set(listings.map((l) => l.propertyId).filter(Boolean));
  const availableProperties = useMemo(() => {
    const all = (adminProps ?? []).filter((p) => !existingPropertyIds.has(p.id));
    if (!listingSearch.trim()) return all.slice(0, 8);
    const q = listingSearch.trim().toLowerCase();
    return all
      .filter(
        (p) => p.address.toLowerCase().includes(q) || (p.city ?? '').toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [adminProps, existingPropertyIds, listingSearch]);
  const handlePickProperty = (p: AdminProperty) => {
    const addr = [p.address, p.city, p.state, p.zipCode].filter(Boolean).join(', ');
    createListingMutDR.mutate(
      {
        dealId: deal.id,
        data: { address: addr, status: 'Touring', propertyId: p.id },
      },
      {
        onSuccess: () => showAlert(t('listing.added'), 'success'),
        onError: () => showAlert(t('listing.add_failed'), 'error'),
      },
    );
    setShowAddListing(false);
    setListingSearch('');
  };
  const closeAddListing = () => {
    setShowAddListing(false);
    setListingSearch('');
    setAddListingTab('new');
    setNewAddr('');
    setNewAddrOptions([]);
    setNewAddrSelected(null);
    setNewAddrParsed(null);
    setNewFile(null);
    setNewReportSource('seller');
    setNewSubmitting(false);
    setNewError(null);
  };
  const handleNewPropertySubmit = async () => {
    if (!newAddrParsed || newSubmitting) return;
    setNewSubmitting(true);
    setNewError(null);
    try {
      const exists = await checkPropertyExists(newAddrParsed);
      if (exists.exists) {
        setNewError('This property already exists.');
        setNewSubmitting(false);
        return;
      }
      const { property } = await createProperty({
        address: newAddrParsed.address,
        city: newAddrParsed.city,
        state: newAddrParsed.state,
        zipCode: newAddrParsed.zipCode,
        latitude: newAddrParsed.latitude,
        longitude: newAddrParsed.longitude,
      });
      if (newFile) {
        beginProcessingIndicator();
        const [presigned] = await generatePreSignedUrls({
          files: [
            {
              filename: newFile.name,
              mimeType: newFile.type || 'application/octet-stream',
              isExtractable: true,
            },
          ],
        });
        await fetch(presigned.preSignedUrl, {
          method: 'PUT',
          body: newFile,
          headers: { 'Content-Type': newFile.type || 'application/octet-stream' },
        });
        await createDocuments({
          propertyId: property.id,
          source: newReportSource,
          documents: [
            {
              filename: newFile.name,
              fileSize: newFile.size,
              mimeType: newFile.type || 'application/octet-stream',
              s3Key: presigned.key,
            },
          ],
        });
      }
      const fullAddr = `${newAddrParsed.address}, ${newAddrParsed.city}, ${newAddrParsed.state} ${newAddrParsed.zipCode}`;
      createListingMutDR.mutate(
        {
          dealId: deal.id,
          data: { address: fullAddr, status: 'Touring', propertyId: property.id },
        },
        {
          onSuccess: () => showAlert(t('listing.added'), 'success'),
          onError: () => showAlert(t('listing.add_failed'), 'error'),
        },
      );
      closeAddListing();
    } catch {
      setNewError('Failed to add property.');
      showAlert(t('property.save_failed'), 'error');
      setNewSubmitting(false);
    }
  };
  const TERMINAL_STATUSES = new Set(['Under Contract', 'Pending', 'Closed', 'Lost', 'Passed']);
  const LOCKED_STATUSES = new Set(['Closed', 'Lost', 'Passed']);
  const updateListing = (id: string, update: Partial<Listing>) => {
    const listing = listings.find((x) => x.id === id);
    if (update.status !== undefined && TERMINAL_STATUSES.has(update.status)) {
      if (update.status === 'Under Contract') setAcceptedPrice('');
      setPendingTerminal({ listingId: id, newStatus: update.status });
      return;
    }
    if (
      update.status !== undefined &&
      LISTING_OFFER_STATUSES.has(update.status) &&
      listing &&
      listing.offersUsed === 0
    ) {
      setPendingOffer({ listingId: id, newStatus: update.status });
      return;
    }
    const { offersUsed, ...statusUpdate } = update;
    const isOffersUsedOnly = offersUsed !== undefined && Object.keys(statusUpdate).length === 0;
    updateListingMutDR.mutate({ listingId: id, data: isOffersUsedOnly ? update : statusUpdate });
  };
  const confirmTerminal = async () => {
    if (!pendingTerminal || terminalSubmitting) return;
    setTerminalSubmitting(true);
    const { listingId, newStatus } = pendingTerminal;
    try {
      // Status update fires first. The transfer below is best-effort; if it
      // fails (network, cascade outage, etc.) the deal still ends up marked
      // Closed and the advisor can run Transfer Ownership later.
      updateListingMutDR.mutate({
        listingId,
        data: {
          status: newStatus,
          ...(newStatus === 'Under Contract'
            ? { acceptedPrice: Number(acceptedPrice.replace(/[^0-9]/g, '')) }
            : {}),
        },
      });

      if (newStatus === 'Closed') {
        const listing = listings.find((l) => l.id === listingId);
        const buyer = members.find((m) => m.id === closeDealBuyerId);
        const propertyId = listing?.propertyId;
        const requestedDocIds = Array.from(closeDealDocIds);

        // Short-circuit before hitting the API in the cases where we know the
        // backend will reject: no buyer selected, no email on the member, no
        // userId (= not yet a confirmed Cognito account), or no propertyId on
        // the listing. The UI already flags "not signed up" inline; matching
        // that with a clean skip toast avoids a guaranteed 400 + console
        // noise on every close.
        if (!buyer?.email || !buyer.userId || !propertyId) {
          showAlert(t('property.close_deal_transfer_skipped'), 'warning');
        } else {
          try {
            const result = await transferProperty(propertyId, buyer.email, requestedDocIds);
            if (result.cascadeError) {
              // Backend caught a thrown cascade and zeroed the counts. Surface
              // it as a warning so the advisor knows ownership moved but docs
              // didn't.
              showAlert(t('property.close_deal_transfer_skipped'), 'warning');
            } else if (result.documentsFailed > 0) {
              showAlert(
                `Property transferred. ${result.documentsFailed} document(s) could not be moved — check the audit log.`,
                'warning',
              );
            } else if (requestedDocIds.length === 0) {
              showAlert(t('property.transfer_succeeded_ownership_only'), 'success');
            } else {
              showAlert(t('property.transfer_succeeded'), 'success');
            }
          } catch (e) {
            console.error('Close-deal transfer failed', e);
            showAlert(t('property.close_deal_transfer_skipped'), 'warning');
          }
        }
      }
    } finally {
      setTerminalSubmitting(false);
      setPendingTerminal(null);
    }
  };
  const confirmOffer = () => {
    if (!pendingOffer) return;
    const listing = listings.find((x) => x.id === pendingOffer.listingId);
    updateListingMutDR.mutate({
      listingId: pendingOffer.listingId,
      data: { status: pendingOffer.newStatus, offersUsed: (listing?.offersUsed ?? 0) + 1 },
    });
    setPendingOffer(null);
  };

  const listingsSection = isOpen ? (
    isMobile ? (
      <div
        style={{
          position: 'relative',
          paddingLeft: 26,
          paddingBottom: 8,
          backgroundColor: 'transparent',
        }}>
        <div
          style={{
            position: 'absolute',
            left: 5,
            top: 0,
            bottom: 0,
            width: 1,
            backgroundColor: 'var(--tx-fill-2)',
          }}
        />
        {listings.map((l) => {
          const lsc = LISTING_STATUS_COLOR[l.status] || '#8B8680';
          const linkedPropMobile = adminProps?.find((p) => p.id === l.propertyId);
          const isLocked = l.propertyId ? lockedSet.has(l.propertyId) : false;
          return (
            <div
              key={l.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                padding: '8px 0',
              }}>
              {l.address ? (
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.text,
                      fontFamily: font,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {l.address.split(',')[0]}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: font }}>
                      {linkedPropMobile?.price
                        ? `$${linkedPropMobile.price.toLocaleString()}`
                        : '—'}
                    </span>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: font }}>
                      {linkedPropMobile?.listedDate
                        ? formatLocalDate(epochSecondsToLocalDateStr(linkedPropMobile.listedDate))
                        : '—'}
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{ flex: 1 }} />
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <div>
                  {isLocked || (l.status === 'Under Contract' && !canEditTerminal) ? (
                    <UnderContractLockChip />
                  ) : LOCKED_STATUSES.has(l.status) && !canAccessAdmin ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: font,
                        color: lsc,
                        background: `${lsc}18`,
                        borderRadius: 6,
                        padding: '4px 10px',
                      }}>
                      {l.status}
                    </span>
                  ) : (
                    <CustomSelect
                      value={l.status}
                      options={LISTING_STATUSES.map((s) => ({ value: s, label: s, color: LISTING_STATUS_COLOR[s] }))}
                      onChange={(v) => updateListing(l.id, { status: v })}
                      // the status as the app's tick on a neutral control, like the role picker
                      triggerStyle={{ background: 'var(--tx-fill)', color: 'var(--tx-text)' }}
                    />
                  )}
                </div>
                {canAccessAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteListingMutDR.mutate(l.id, {
                        onSuccess: () => showAlert(t('listing.removed'), 'success'),
                        onError: () => showAlert(t('listing.remove_failed'), 'error'),
                      });
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: C.subtle,
                      padding: 4,
                    }}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
        <button
          onClick={() => setShowAddListing(true)}
          style={{
            marginTop: 14,
            width: 'fit-content',
            fontSize: 11,
            fontWeight: 600,
            color: C.subtle,
            background: 'var(--tx-fill)',
            border: 'none',
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            fontFamily: font,
          }}>
          <span style={{ color: '#c0ff02' }}>+</span> Add Property
        </button>
      </div>
    ) : (
      <div style={{ position: 'relative' }}>
        {/* Vertical tree line centered under chevron column */}
        <div
          style={{
            position: 'absolute',
            left: 14,
            top: 0,
            bottom: 32,
            width: 1,
            background: 'var(--tx-fill-2)',
            pointerEvents: 'none',
          }}
        />
        {listings.map((l) => {
          const lsc = LISTING_STATUS_COLOR[l.status] || '#8B8680';
          const linkedProp = adminProps?.find((p) => p.id === l.propertyId);
          const isLocked = l.propertyId ? lockedSet.has(l.propertyId) : false;
          return (
            <div
              key={l.id}
              className="heq-listing-row"
              style={{
                display: 'grid',
                gridTemplateColumns: MG,
                gap: 25,
                alignItems: 'center',
                padding: '8px 0',
              }}>
              <div />
              {/* Col 2: address + price/listed sub-info + delete */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.text,
                      fontFamily: font,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {l.address.split(',')[0]}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: font }}>
                      {linkedProp?.price ? `$${linkedProp.price.toLocaleString()}` : '—'}
                    </span>
                    <span style={{ fontSize: 11, color: C.muted, fontFamily: font }}>
                      {linkedProp?.listedDate
                        ? formatLocalDate(epochSecondsToLocalDateStr(linkedProp.listedDate))
                        : '—'}
                    </span>
                  </div>
                </div>
                {canAccessAdmin && (
                  <button
                    className="heq-edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteListingMutDR.mutate(l.id, {
                        onSuccess: () => showAlert(t('listing.removed'), 'success'),
                        onError: () => showAlert(t('listing.remove_failed'), 'error'),
                      });
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      background: 'var(--tx-fill)',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      color: C.subtle,
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Col 3: empty */}
              <div />
              {/* Col 4: STATUS — aligned with deal STATUS column */}
              <div>
                {isLocked || (l.status === 'Under Contract' && !canEditTerminal) ? (
                  <UnderContractLockChip inline />
                ) : LOCKED_STATUSES.has(l.status) && !canAccessAdmin ? (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: font,
                      color: lsc,
                      background: `${lsc}18`,
                      borderRadius: 6,
                      padding: '4px 10px',
                    }}>
                    {l.status}
                  </span>
                ) : (
                  <CustomSelect
                    value={l.status}
                    options={LISTING_STATUSES.map((s) => ({ value: s, label: s, color: LISTING_STATUS_COLOR[s] }))}
                    onChange={(v) => updateListing(l.id, { status: v })}
                    // the status as the app's tick on a neutral control, like the role picker
                    triggerStyle={{ background: 'var(--tx-fill)', color: 'var(--tx-text)' }}
                  />
                )}
              </div>
              {/* Col 5: empty (PERIOD) */}
              <div />
              {/* Col 6: OFFERS — auto-derived, show count only */}
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: l.offersUsed > 0 ? L.moderate : C.subtle,
                  fontFamily: font,
                }}>
                {l.offersUsed > 0 ? `${l.offersUsed}` : '—'}
              </div>
              {/* Col 7: empty */}
              <div />
              {/* Cols 7–8: empty */}
              <div />
            </div>
          );
        })}
        {/* Add Property — placed in address column */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: MG,
            gap: 25,
            alignItems: 'center',
            paddingTop: 10,
            paddingBottom: 10,
          }}>
          <div />
          <button
            onClick={() => setShowAddListing(true)}
            style={{
              width: 'fit-content',
              fontSize: 11,
              fontWeight: 600,
              color: C.subtle,
              background: 'var(--tx-fill)',
              border: 'none',
              borderRadius: 6,
              padding: '5px 10px',
              cursor: 'pointer',
              fontFamily: font,
              textAlign: 'left' as const,
            }}>
            <span style={{ color: '#c0ff02' }}>+</span> Add Property
          </button>
        </div>
      </div>
    )
  ) : null;

  useEffect(() => {
    if (!newAddr || newAddr.length < 3 || newAddrSelected) {
      if (!newAddr || newAddr.length < 3) setNewAddrOptions([]);
      return;
    }
    if (!placesLibDR) return;

    const timer = setTimeout(async () => {
      try {
        const suggestions = await fetchAddressSuggestions(newAddr, placesLibDR);
        setNewAddrOptions(suggestions);
      } catch {
        setNewAddrOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [newAddr, newAddrSelected, placesLibDR]);

  useEffect(() => {
    if (!newAddrSelected) {
      setNewAddrParsed(null);
      return;
    }
    if (!geocoderDR) return;

    let cancelled = false;
    geocoderDR.geocode({ placeId: newAddrSelected.place_id }, (results, status) => {
      if (cancelled) return;
      if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
        setNewAddrParsed(null);
        return;
      }
      const result = results[0];
      const components = parseGoogleAddressComponents(result.address_components);
      const address =
        [components.streetNumber, components.street].filter(Boolean).join(' ').trim() ||
        result.formatted_address ||
        '';
      const city = components.city ?? '';
      const state = components.state ?? '';
      if (!address || !city || !state) {
        setNewAddrParsed(null);
        return;
      }
      setNewAddrParsed({
        address,
        city,
        state,
        zipCode: components.zip ?? '',
        latitude: result.geometry.location.lat(),
        longitude: result.geometry.location.lng(),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [newAddrSelected, geocoderDR]);

  const addListingDialog = showAddListing ? (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 10px',
        background: 'var(--tx-backdrop)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={closeAddListing}>
      <div
        style={{
          background: 'transparent',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: '24px',
          width: '100%',
          maxWidth: 480,
          maxHeight: 560,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div
          style={{
            padding: '25px 25px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <span style={{ fontSize: 17, fontWeight: 600, color: 'var(--tx-text)', fontFamily: font }}>
            Add a property
          </span>
          <CloseButton onClose={closeAddListing} label="Close" />
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--tx-muted)',
            fontFamily: font,
            padding: '6px 25px 16px',
            lineHeight: 1.5,
          }}>
          Add an existing property or create a new one for this deal.
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, padding: '0 25px 12px' }}>
          {(['new', 'existing'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setAddListingTab(t)}
              style={{
                flex: 1,
                padding: '8px 0',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: font,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: addListingTab === t ? 'var(--tx-text)' : 'var(--tx-faint)',
                borderBottom: addListingTab === t ? '2px solid var(--tx-line)' : '2px solid transparent',
                transition: 'color 0.15s',
              }}>
              {t === 'existing' ? 'Existing Property' : 'New Property'}
            </button>
          ))}
        </div>

        {/* Existing tab */}
        {addListingTab === 'existing' && (
          <>
            <div style={{ padding: '15px 25px 8px' }}>
              <input
                autoFocus
                value={listingSearch}
                onChange={(e) => setListingSearch(e.target.value)}
                placeholder="Search properties..."
                style={{
                  width: '100%',
                  height: 36,
                  background: 'var(--tx-fill)',
                  border: 'none',
                  borderRadius: 10,
                  padding: '0 14px',
                  color: 'var(--tx-text)',
                  fontSize: 14,
                  fontFamily: font,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 13px 12px' }}>
              {availableProperties.length === 0 ? (
                <div
                  style={{
                    padding: 16,
                    textAlign: 'center',
                    color: 'var(--tx-faint)',
                    fontSize: 12,
                    fontFamily: font,
                  }}>
                  No matching properties
                </div>
              ) : (
                availableProperties.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => handlePickProperty(p)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontFamily: font,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-text)' }}>
                      {p.address}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tx-faint)', marginTop: 2 }}>
                      {[p.city, p.state, p.zipCode].filter(Boolean).join(', ')}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {/* New tab */}
        {addListingTab === 'new' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '15px 25px 25px' }}>
            <Autocomplete
              freeSolo
              options={newAddrOptions}
              getOptionLabel={(o: any) => (typeof o === 'string' ? o : o.description ?? '')}
              value={newAddrSelected}
              onChange={(_, v) => {
                if (typeof v !== 'string') setNewAddrSelected(v);
              }}
              inputValue={newAddr}
              onInputChange={(_, v) => {
                setNewAddr(v);
                if (newAddrSelected) setNewAddrSelected(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Start typing an address..."
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontFamily: font,
                      fontSize: 14,
                      color: 'var(--tx-text)',
                      backgroundColor: 'var(--tx-fill)',
                      borderRadius: '10px',
                      height: 36,
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--tx-line)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input::placeholder': { color: '#737373', opacity: 1 },
                  }}
                />
              )}
              renderOption={(props, option: any) => (
                <li
                  {...props}
                  key={option.place_id}
                  style={{
                    fontFamily: font,
                    fontSize: 13,
                    color: 'var(--tx-text)',
                    padding: '10px 16px',
                  }}>
                  {option.description}
                </li>
              )}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: 'var(--tx-fill)',
                    borderRadius: '12px',
                    mt: '4px',
                    '& .MuiAutocomplete-listbox': { py: '6px' },
                    '& .MuiAutocomplete-option': {
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                    },
                  },
                },
              }}
            />

            {newAddrParsed && (
              <div
                style={{
                  marginTop: 12,
                  padding: '12px 14px',
                  background: 'var(--tx-fill)',
                  borderRadius: 10,
                }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-text)', fontFamily: font }}>
                  {newAddrParsed.address}
                </div>
                <div style={{ fontSize: 12, color: 'var(--tx-muted)', fontFamily: font, marginTop: 2 }}>
                  {newAddrParsed.city}, {newAddrParsed.state} {newAddrParsed.zipCode}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--tx-muted)',
                  fontFamily: font,
                  marginBottom: 8,
                }}>
                Inspection Report{' '}
                <span style={{ fontWeight: 400, color: '#737373' }}>(optional)</span>
              </div>
              {newFile ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '10px 14px',
                    background: 'var(--tx-fill)',
                    borderRadius: 10,
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--tx-text)',
                        fontFamily: font,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {newFile.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#737373', fontFamily: font }}>
                      {(newFile.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      {(['seller', 'buyer'] as const).map((opt) => {
                        const active = newReportSource === opt;
                        return (
                          <div
                            key={opt}
                            onClick={() => setNewReportSource(opt)}
                            style={{
                              fontSize: 11,
                              fontFamily: font,
                              padding: '3px 10px',
                              borderRadius: 6,
                              border: 0,
                              background: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                              color: active ? 'var(--tx-text)' : 'var(--tx-faint)',
                              fontWeight: active ? 600 : 400,
                              cursor: 'pointer',
                              letterSpacing: '-0.01em',
                              transition: 'all 0.15s',
                            }}>
                            {opt === 'seller' ? 'Seller' : 'Buyer'}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <span
                    onClick={() => setNewFile(null)}
                    style={{ cursor: 'pointer', color: '#737373', fontSize: 14, marginTop: 2 }}>
                    ✕
                  </span>
                </div>
              ) : (
                <div
                  onClick={() => document.getElementById(`add-listing-file-${deal.id}`)?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.borderColor = 'rgba(192,255,2,0.5)';
                    e.currentTarget.style.backgroundColor = 'rgba(192,255,2,0.04)';
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                    const f = e.dataTransfer.files?.[0];
                    if (!f) return;
                    if (f.size > 25 * 1024 * 1024) {
                      setNewError('File must be under 25MB');
                      return;
                    }
                    setNewFile(f);
                    setNewError(null);
                  }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    padding: '28px 14px',
                    borderRadius: '12px',
                    border: '1.5px dashed var(--tx-line)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}>
                  <span style={{ fontSize: 12, color: '#737373', fontFamily: font }}>
                    Drag & drop or click to upload
                  </span>
                  <span style={{ fontSize: 10, color: '#555', fontFamily: font }}>
                    PDF, DOCX, JPG, or PNG (max 25MB)
                  </span>
                </div>
              )}
              <input
                id={`add-listing-file-${deal.id}`}
                type="file"
                accept=".pdf,.docx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 25 * 1024 * 1024) {
                    setNewError('File must be under 25MB');
                    return;
                  }
                  setNewFile(f);
                  setNewError(null);
                }}
                style={{ display: 'none' }}
              />
            </div>

            {newError && (
              <div style={{ fontSize: 12, color: '#ff6b6b', fontFamily: font, marginTop: 10 }}>
                {newError}
              </div>
            )}

            <div
              onClick={newAddrParsed && !newSubmitting ? handleNewPropertySubmit : undefined}
              style={{
                marginTop: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                height: 44,
                borderRadius: '12px',
                background: newAddrParsed ? 'var(--tx-lime)' : 'var(--tx-fill)',
                color: newAddrParsed ? 'var(--tx-on-lime)' : 'var(--tx-faint)',
                fontFamily: font,
                fontSize: 14,
                fontWeight: 600,
                cursor: newAddrParsed && !newSubmitting ? 'pointer' : 'default',
                transition: 'all 0.15s',
              }}>
              {newSubmitting ? (
                <CircularProgress size={18} sx={{ color: '#262626' }} />
              ) : (
                '+ Add Property'
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  ) : null;

  const terminalListing = listings.find((x) => x.id === pendingTerminal?.listingId);
  const TERMINAL_MESSAGES: Record<string, { title: string; body: string; cta: string }> = {
    'Under Contract': {
      title: 'Mark as Under Contract',
      body: 'This will mark the property as Under Contract.',
      cta: 'Confirm',
    },
    Pending: {
      title: 'Mark as Pending',
      body: 'This will mark the property as Pending.',
      cta: 'Confirm',
    },
    Closed: {
      title: 'Close deal',
      body: 'This will mark the property as Closed (won). The client will retain access.',
      cta: 'Close & Activate',
    },
    Lost: {
      title: 'Mark as Lost',
      body: "This will revoke the client's access to this property. The listing will remain on the deal as a record.",
      cta: 'Confirm lost',
    },
    Passed: {
      title: 'Mark as Passed',
      body: "This will revoke the client's access to this property. The listing will remain on the deal as a record.",
      cta: 'Confirm passed',
    },
  };
  const terminalConfirmDialog =
    pendingTerminal &&
    createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--tx-backdrop)',
        }}
        onClick={() => setPendingTerminal(null)}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'transparent',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderRadius: '24px',
            padding: '25px',
            // Close Deal needs more room for the buyer dropdown + doc picker;
            // the other terminal statuses keep the original compact width.
            width: isCloseDeal ? 560 : 480,
            maxWidth: 'calc(100vw - 40px)',
            maxHeight: 'calc(100vh - 80px)',
            overflowY: 'auto',
            fontFamily: font,
            transform: `translateX(${terminalDialogShiftX}px)`,
            transition: 'transform 0.2s ease-in-out',
          }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: C.text,
              marginBottom: 10,
            }}>
            {TERMINAL_MESSAGES[pendingTerminal.newStatus]?.title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
              {(terminalListing?.address || 'this listing').split(',')[0]}
            </span>
            <span style={{ fontSize: 13, color: C.muted }}>→</span>
            <span
              style={{
                background: `${LISTING_STATUS_COLOR[pendingTerminal.newStatus] || C.muted}18`,
                color: LISTING_STATUS_COLOR[pendingTerminal.newStatus] || C.muted,
                borderRadius: 6,
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 600,
              }}>
              {pendingTerminal.newStatus}
            </span>
          </div>
          <div
            style={{
              fontSize: 14,
              color: C.muted,
              lineHeight: 1.5,
              marginBottom: isCloseDeal
                ? 0
                : pendingTerminal.newStatus === 'Under Contract'
                ? 12
                : 24,
            }}>
            {TERMINAL_MESSAGES[pendingTerminal.newStatus]?.body}
          </div>
          {pendingTerminal.newStatus === 'Under Contract' && (
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  marginBottom: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.muted,
                  fontFamily: font,
                }}>
                Accepted Purchase Price
              </div>
              <input
                aria-label="Accepted purchase price"
                inputMode="numeric"
                value={acceptedPrice}
                onChange={(e) => setAcceptedPrice(formatCurrencyInput(e.target.value))}
                placeholder="$0"
                autoFocus
                style={{ ...inputStyle, width: '100%', height: 40, boxSizing: 'border-box' }}
              />
            </div>
          )}
          {isCloseDeal && (
            <>
              {/* Divider between close confirmation and transfer section */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '30px 0 28px' }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: C.muted,
                    fontFamily: font,
                  }}>
                  Activate Home HQ
                </span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
              </div>
              {/* Buyer dropdown — every member is listed; ones without a userId
                  are flagged so the advisor knows the transfer will be skipped
                  and they should run Transfer Ownership later. */}
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.muted,
                    fontFamily: font,
                    marginBottom: 8,
                  }}>
                  Activate for
                </div>
                {(() => {
                  const signedUp = members.filter((m) => m.userId);
                  const fieldStyle: React.CSSProperties = {
                    width: '100%',
                    height: 40,
                    borderRadius: 10,
                    background: 'var(--tx-fill)',
                    border: 'none',
                    fontFamily: font,
                    fontSize: 13,
                    boxSizing: 'border-box',
                  };
                  if (signedUp.length === 0) {
                    return (
                      <div
                        style={{
                          ...fieldStyle,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 12px',
                          color: C.muted,
                        }}>
                        No signed-up members — transfer will be skipped.
                      </div>
                    );
                  }
                  if (signedUp.length === 1) {
                    const m = signedUp[0];
                    return (
                      <div
                        style={{
                          ...fieldStyle,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 12px',
                          color: C.text,
                        }}>
                        {m.name || m.email}
                        {m.name && m.email ? ` (${m.email})` : ''}
                      </div>
                    );
                  }
                  return (
                    <select
                      value={closeDealBuyerId}
                      onChange={(e) => setCloseDealBuyerId(e.target.value)}
                      style={{
                        ...fieldStyle,
                        color: C.text,
                        padding: '0 32px 0 12px',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23bbb7af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                      }}>
                      {signedUp.map((m) => (
                        <option key={m.id} value={m.id} style={{ background: '#262626' }}>
                          {m.name || m.email}
                          {m.name && m.email ? ` (${m.email})` : ''}
                        </option>
                      ))}
                    </select>
                  );
                })()}
              </div>

              {/* Documents to transfer — same picker as the Properties page. All
                  eligible docs are pre-checked; advisor unchecks anything they
                  want to keep with the seller side. */}
              <div style={{ marginBottom: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.muted,
                      fontFamily: font,
                    }}>
                    Documents to transfer ({closeDealDocIds.size}/{closeDocs.length})
                  </div>
                  {closeDocs.length > 0 && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => setCloseDealDocIds(new Set(closeDocs.map((r: any) => r.id)))}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: C.muted,
                          fontSize: 11,
                          fontFamily: font,
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                        }}>
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={() => setCloseDealDocIds(new Set())}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: C.muted,
                          fontSize: 11,
                          fontFamily: font,
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                        }}>
                        Select none
                      </button>
                    </div>
                  )}
                </div>
                {closeDocs.length === 0 ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: C.muted,
                      fontFamily: font,
                      padding: '10px 12px',
                      background: 'var(--tx-fill)',
                      border: 'none',
                      borderRadius: 10,
                    }}>
                    No documents uploaded for this property yet.
                  </div>
                ) : (
                  <div
                    style={{
                      height: closeDocs.length === 1 ? 40 : undefined,
                      maxHeight: closeDocs.length === 1 ? undefined : 200,
                      overflowY: closeDocs.length === 1 ? 'hidden' : 'auto',
                      display: closeDocs.length === 1 ? 'flex' : undefined,
                      alignItems: closeDocs.length === 1 ? 'center' : undefined,
                      background: 'var(--tx-fill)',
                      border: 'none',
                      borderRadius: 10,
                    }}>
                    {closeDocs.map((r: any, i: number) => {
                      const checked = closeDealDocIds.has(r.id);
                      const isLast = i === closeDocs.length - 1;
                      return (
                        <div
                          key={r.id}
                          onClick={() =>
                            setCloseDealDocIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(r.id)) next.delete(r.id);
                              else next.add(r.id);
                              return next;
                            })
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '0 12px',
                            height: 40,
                            width: '100%',
                            cursor: 'pointer',
                            borderBottom: isLast ? 'none' : `1px solid ${C.border}`,
                            fontFamily: font,
                            boxSizing: 'border-box',
                          }}>
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 4,
                              border: checked ? 'none' : `1.5px solid ${C.border}`,
                              background: checked ? 'var(--tx-lime)' : 'transparent',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                            {checked && (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                <polyline
                                  points="2,6 5,9 10,3"
                                  stroke="#262626"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              color: C.text,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                              minWidth: 0,
                            }}>
                            {r.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 30 }}>
            <button
              onClick={() => setPendingTerminal(null)}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 10,
                background: 'var(--tx-fill)',
                border: 'none',
                color: C.muted,
                fontFamily: font,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                cursor: 'pointer',
              }}>
              Cancel
            </button>
            <button
              onClick={confirmTerminal}
              disabled={
                terminalSubmitting ||
                (pendingTerminal.newStatus === 'Under Contract' &&
                  Number(acceptedPrice.replace(/[^0-9]/g, '')) <= 0)
              }
              style={{
                flex: 2,
                height: 40,
                borderRadius: 10,
                background: LISTING_STATUS_COLOR[pendingTerminal.newStatus] || C.lime,
                border: 'none',
                color: 'var(--tx-text)',
                fontFamily: font,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                cursor:
                  terminalSubmitting ||
                  (pendingTerminal.newStatus === 'Under Contract' &&
                    Number(acceptedPrice.replace(/[^0-9]/g, '')) <= 0)
                    ? 'not-allowed'
                    : 'pointer',
                opacity:
                  terminalSubmitting ||
                  (pendingTerminal.newStatus === 'Under Contract' &&
                    Number(acceptedPrice.replace(/[^0-9]/g, '')) <= 0)
                    ? 0.6
                    : 1,
              }}>
              {terminalSubmitting ? 'Working…' : TERMINAL_MESSAGES[pendingTerminal.newStatus]?.cta}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  const pendingListing = listings.find((x) => x.id === pendingOffer?.listingId);
  const offerConfirmDialog =
    pendingOffer &&
    createPortal(
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--tx-backdrop)',
        }}
        onClick={() => setPendingOffer(null)}>
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'transparent',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderRadius: '24px',
            padding: '25px',
            width: 480,
            maxWidth: 'calc(100vw - 40px)',
            fontFamily: font,
          }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: C.text,
              marginBottom: 16,
            }}>
            Confirm offer
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
              {(pendingListing?.address || 'this listing').split(',')[0]}
            </span>
            <span style={{ fontSize: 13, color: C.muted }}>→</span>
            <span
              style={{
                background: `${LISTING_STATUS_COLOR[pendingOffer.newStatus] || C.muted}18`,
                color: LISTING_STATUS_COLOR[pendingOffer.newStatus] || C.muted,
                borderRadius: 6,
                padding: '2px 8px',
                fontSize: 11,
                fontWeight: 600,
              }}>
              {pendingOffer.newStatus}
            </span>
          </div>
          <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5, marginBottom: 24 }}>
            This will permanently count as{' '}
            <span style={{ color: C.muted, fontWeight: 600 }}>1 offer used</span> against this
            search period. This cannot be undone.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => setPendingOffer(null)}
              style={{
                flex: 1,
                height: 40,
                borderRadius: 10,
                background: 'var(--tx-fill)',
                border: 'none',
                color: C.muted,
                fontFamily: font,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
              }}>
              Cancel
            </button>
            <button
              onClick={confirmOffer}
              style={{
                flex: 2,
                height: 40,
                borderRadius: 10,
                background: C.muted,
                border: 'none',
                color: 'var(--tx-text)',
                fontFamily: font,
                fontSize: 14,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                cursor: 'pointer',
              }}>
              Confirm offer
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  if (isMobile) {
    return (
      <>
        {terminalConfirmDialog}
        {offerConfirmDialog}
        {addListingDialog}
        <div
          className="heq-deal-row"
          style={{ padding: '14px 0' }}
          onClick={() => setIsOpen((o) => !o)}>
          {/* Row 1: chevron + deal name + fee */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div
              style={{
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: C.subtle,
                flexShrink: 0,
                marginTop: 3,
              }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: font }}>
                {deal.name}
              </div>
              <div style={{ fontSize: 11, color: C.subtle, fontFamily: font, marginTop: 2 }}>
                Started {fmtDate(deal.engagementStart)}
              </div>
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: C.text,
                fontFamily: font,
                flexShrink: 0,
              }}>
              {fmtMoney(fee)}
            </div>
          </div>
          {/* Co-advisor row (mobile) */}
          <div style={{ paddingLeft: 18, marginBottom: 8 }}>
            <CoAdvisorSelect
              value={deal.coAdvisorIds ?? []}
              advisors={advisors}
              primaryAdvisorId={primaryAdvisorId}
              onChange={(ids) => onUpdate?.({ coAdvisorIds: ids })}
              editable={isOwner || canAccessAdmin}
            />
          </div>
          {/* Row 2: status dots + period + expiry */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingLeft: 18,
              marginTop: 10,
              marginBottom: 10,
            }}>
            {deal.listings && deal.listings.length > 0 ? (
              <div style={{ display: 'flex', gap: 4 }}>
                {deal.listings.map((l) => (
                  <ListingStatusDot key={l.id} status={l.status} showTooltip />
                ))}
              </div>
            ) : (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: font,
                  color: STATUS_COLOR[effectiveDealStatus(deal)] || C.muted,
                  background: `${STATUS_COLOR[effectiveDealStatus(deal)] || C.muted}18`,
                  borderRadius: 6,
                  padding: '4px 10px',
                }}>
                {effectiveDealStatus(deal)}
              </span>
            )}
            <CustomSelect
              value={deal.periodType}
              options={PERIOD_TYPES.map((p) => ({ value: p, label: p }))}
              onChange={(v) => onUpdate?.({ periodType: v })}
              triggerStyle={{
                background: 'var(--tx-fill-2)',
                color: 'var(--tx-muted)',
                border: 'none',
              }}
            />
            <ExpiryIndicator days={effectiveDealStatus(deal) === 'Closed' ? null : days} />
          </div>
          {/* Row 3: offers label + pills — always own line */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              paddingLeft: 18,
              marginTop: 6,
            }}>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.muted,
                fontFamily: font,
              }}>
              Offers
            </span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(6, 8px)',
                gap: 3,
              }}>
              {Array.from({ length: maxOffers }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: 8,
                    height: 4,
                    borderRadius: 2,
                    background: i < dealEffectiveOffersUsed(deal) ? L.moderate : 'var(--tx-fill-2)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
        {isOpen && listingsSection}
      </>
    );
  }

  return (
    <>
      <div
        className="heq-deal-row"
        style={{
          display: 'grid',
          gridTemplateColumns: MG,
          padding: '12px 0',
          alignItems: 'center',
          backgroundColor: 'transparent',
          gap: 25,
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setIsOpen((o) => !o)}>
          <svg
            style={{
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: C.subtle,
            }}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: font }}>
            {deal.name}
          </div>
          <div style={{ fontSize: 11, color: C.subtle, fontFamily: font, marginTop: 2 }}>
            Started {fmtDate(deal.engagementStart)}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, overflow: 'hidden' }}>
          <CoAdvisorSelect
            value={deal.coAdvisorIds ?? []}
            advisors={advisors}
            primaryAdvisorId={primaryAdvisorId}
            onChange={(ids) => onUpdate?.({ coAdvisorIds: ids })}
            editable={isOwner || canAccessAdmin}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {deal.listings && deal.listings.length > 0 ? (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {deal.listings.map((l) => (
                <ListingStatusDot key={l.id} status={l.status} showTooltip />
              ))}
            </div>
          ) : (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                fontFamily: font,
                color: STATUS_COLOR[effectiveDealStatus(deal)] || C.muted,
                background: `${STATUS_COLOR[effectiveDealStatus(deal)] || C.muted}18`,
                borderRadius: 6,
                padding: '4px 10px',
              }}>
              {effectiveDealStatus(deal)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CustomSelect
            value={deal.periodType}
            options={PERIOD_TYPES.map((p) => ({ value: p, label: p }))}
            onChange={(v) => onUpdate?.({ periodType: v })}
            triggerStyle={{
              background: 'var(--tx-fill-2)',
              color: 'var(--tx-muted)',
              border: 'none',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {deal.listings && deal.listings.length > 0 ? (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: dealEffectiveOffersUsed(deal) >= maxOffers ? L.critical : C.text,
                  fontFamily: font,
                }}>
                {dealEffectiveOffersUsed(deal)}
              </span>
            ) : (
              <CustomSelect
                value={deal.offersUsed}
                options={Array.from({ length: maxOffers + 1 }, (_, i) => ({
                  value: i,
                  label: String(i),
                }))}
                onChange={(v) => onUpdate?.({ offersUsed: Number(v) })}
                showChevron={false}
                triggerStyle={{
                  background: 'transparent',
                  color: deal.offersUsed >= maxOffers ? L.critical : C.text,
                  border: 'none',
                  padding: '0',
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            )}
            <span style={{ color: C.subtle, fontSize: 12 }}>/ {maxOffers}</span>
          </div>
          {/* Fixed 6-pill grid so 12-offer deals render as a balanced 2x6
              instead of a single row that wraps with an orphan pill. */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 8px)',
              gap: 3,
            }}>
            {Array.from({ length: maxOffers }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 4,
                  borderRadius: 2,
                  background: i < dealEffectiveOffersUsed(deal) ? L.moderate : 'var(--tx-fill-2)',
                }}
              />
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExpiryIndicator days={effectiveDealStatus(deal) === 'Closed' ? null : days} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: font }}>
              {fmtMoney(fee)}
            </div>
            {deal.feeType === 'Percentage' ? (
              <div style={{ fontSize: 10, color: C.subtle, fontFamily: font }}>
                {((deal.commissionRate ?? 0) * 100).toLocaleString()}%
              </div>
            ) : (
              deal.feeOverride != null && (
                <div style={{ fontSize: 10, color: C.subtle, fontFamily: font }}>manual</div>
              )
            )}
          </div>
          <button
            className="heq-edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 6,
              background: 'var(--tx-fill)',
              border: 'none',
              color: C.subtle,
              cursor: 'pointer',
              flexShrink: 0,
              marginLeft: 'auto',
            }}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
      </div>
      {listingsSection}
      {terminalConfirmDialog}
      {offerConfirmDialog}
      {addListingDialog}
    </>
  );
}

function MemberRow({ member, onChatClick }: { member: Member; onChatClick: () => void }) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (isMobile) {
    return (
      <div
        className="heq-member-row"
        style={{
          padding: '12px 0',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
        <ClientAvatar name={member.name} size={28} photoUrl={member.photoUrl} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: C.text,
                fontFamily: font,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {member.name}
            </div>
            <MemberRoleBadge role={member.role} />
            {!member.userId && <InvitePendingBadge />}
          </div>
          {!member.userId && (
            <div style={{ marginTop: 6 }}>
              <ResendInviteButton email={member.email} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {member.phone && (
            <a
              href={`tel:${member.phone}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                borderRadius: 8,
                background: 'var(--tx-fill)',
                border: 'none',
                color: C.muted,
                textDecoration: 'none',
              }}
              onClick={(e) => e.stopPropagation()}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            </a>
          )}
          <button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 8,
              background: 'var(--tx-fill)',
              border: 'none',
              color: C.muted,
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              onChatClick();
            }}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="heq-member-row"
      style={{
        display: 'grid',
        gridTemplateColumns: MG,
        padding: '11px 0',
        alignItems: 'center',
        backgroundColor: 'transparent',
        gap: 25,
      }}>
      <div />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <ClientAvatar name={member.name} size={28} photoUrl={member.photoUrl} />
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: C.text,
                fontFamily: font,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {member.name}
            </div>
            {!member.userId && <InvitePendingBadge />}
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.subtle,
              fontFamily: font,
              marginTop: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {member.email}
          </div>
        </div>
      </div>
      <div />
      <div style={{ fontSize: 13, color: C.muted, fontFamily: font }}>
        {formatPhone(member.phone)}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <MemberRoleBadge role={member.role} />
        {!member.userId && <ResendInviteButton email={member.email} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {member.phone && (
          <a
            href={`tel:${member.phone}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: 8,
              background: 'var(--tx-fill)',
              border: 'none',
              color: C.muted,
              textDecoration: 'none',
            }}
            onClick={(e) => e.stopPropagation()}>
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </a>
        )}
        <button
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'var(--tx-fill)',
            border: 'none',
            color: C.muted,
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onChatClick();
          }}>
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      </div>
      <div />
    </div>
  );
}

function EditDealDrawer({
  deal,
  onClose,
  onSave,
}: {
  deal: Deal;
  onClose: () => void;
  onSave: (updated: UpdateDealRequest) => void;
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [name, setName] = useState(deal.name);
  const [engagementStart, setEngagementStart] = useState<Date | null>(
    deal.engagementStart ? new Date(deal.engagementStart) : null,
  );
  const [feeOverride, setFeeOverride] = useState<string>(
    deal.feeOverride != null ? formatCurrencyInput(deal.feeOverride) : '',
  );
  const [feeType, setFeeType] = useState<DealFeeType>(deal.feeType ?? 'Flat');
  const [commissionPercent, setCommissionPercent] = useState<string>(
    String((deal.commissionRate ?? 0.025) * 100),
  );
  const [pendingFeeType, setPendingFeeType] = useState<DealFeeType | null>(null);

  const applyFeeType = (nextFeeType: DealFeeType) => {
    setFeeType(nextFeeType);
    if (nextFeeType === 'Flat') {
      setCommissionPercent('2.5');
    } else {
      setFeeOverride('');
    }
    setPendingFeeType(null);
  };

  const handleSave = () => {
    if (
      pendingFeeType ||
      (feeType === 'Percentage' && !isValidCommissionPercent(commissionPercent))
    )
      return;
    const commonUpdate: UpdateDealRequest = {
      name: name.trim() || deal.name,
      engagementStart: engagementStart
        ? engagementStart.toISOString().split('T')[0]
        : deal.engagementStart,
    };
    onSave({
      ...commonUpdate,
      ...buildDealFeePayload(
        feeType,
        feeOverride.trim() === '' ? null : Number(feeOverride.replace(/[^0-9]/g, '')),
        commissionPercent,
      ),
    });
    onClose();
  };

  const pickerPopperSx = {
    zIndex: 10001,
    '& .MuiPaper-root': {
      background: 'transparent',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderRadius: '16px',
    },
    '& .MuiPickersCalendarHeader-label': { fontFamily: font, fontWeight: 600, color: 'var(--tx-text)' },
    '& .MuiPickersArrowSwitcher-button': {
      color: 'var(--tx-muted)',
      '&:hover': { background: 'rgba(255,255,255,0.08)' },
    },
    '& .MuiDayCalendar-weekDayLabel': {
      fontFamily: font,
      fontSize: 11,
      color: 'var(--tx-faint)',
      fontWeight: 600,
    },
    '& .MuiPickersDay-root': {
      fontFamily: font,
      fontSize: 12,
      fontWeight: 600,
      color: 'var(--tx-muted)',
      background: 'transparent',
      borderRadius: '8px',
      '&:hover': { background: 'rgba(255,255,255,0.08)' },
      '&.Mui-selected': { background: '#c0ff02 !important', color: '#262626', fontWeight: 600 },
      '&.MuiPickersDay-today': { border: '1px solid rgba(192,255,2,0.4)', color: 'var(--tx-text)' },
    },
  };

  const sLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: C.muted,
    fontFamily: font,
    marginBottom: 8,
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'var(--tx-backdrop)' }} />
      <div
        style={{
          // a floating sheet on the frame's 20px gutter, like the right panel: glass, top light, blur, raised. It
          // must be opaque enough that the page under it never reads through
          position: 'relative',
          width: isMobile ? '100%' : 400,
          margin: isMobile ? '0 0 0 auto' : '20px 20px 20px auto',
          borderRadius: isMobile ? 0 : 24,
          overflow: 'hidden',
          background: 'var(--tx-glass-panel)',
          backgroundImage: 'var(--tx-surface-light)',
          backdropFilter: 'var(--tx-blur-panel)',
          WebkitBackdropFilter: 'var(--tx-blur-panel)',
          boxShadow: 'var(--tx-shadow)',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <div
          style={{
            padding: '24px 24px 20px',
            borderBottom: '1px solid var(--tx-line)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <div>
            <div style={{ ...sLabel, marginBottom: 4 }}>Edit deal</div>
            <div style={{ fontSize: 20, fontWeight: 600, color: C.text, fontFamily: font }}>
              {deal.name}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--tx-fill)',
              border: 'none',
              borderRadius: 8,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: C.muted,
              flexShrink: 0,
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            scrollbarWidth: 'none',
          }}>
          <div>
            <div style={sLabel}>Deal Name</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 123 Main St"
              style={{ ...inputStyle, height: 38, width: '100%', boxSizing: 'border-box' }}
              onFocus={(e) => {
                e.target.style.borderColor = C.muted;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = C.border;
              }}
            />
          </div>
          <div>
            <div style={sLabel}>Engagement Start</div>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={engagementStart}
                onChange={(d) => setEngagementStart(d)}
                slots={{
                  openPickerIcon: () => (
                    <SvgIcon sx={{ width: 16, height: 16, color: '#737373' }}>
                      <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11zM7 11h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2zM7 15h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
                    </SvgIcon>
                  ),
                }}
                slotProps={{
                  textField: { size: 'small', fullWidth: true, variant: 'outlined', sx: inputSx },
                  popper: { sx: pickerPopperSx },
                  openPickerButton: {
                    sx: {
                      borderRadius: '50%',
                      mr: '-10px',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          <div>
            <div style={sLabel}>Fee Model</div>
            <PillToggle
              options={['Flat Fee', 'Percentage']}
              value={feeType === 'Flat' ? 'Flat Fee' : 'Percentage'}
              onChange={(value) => {
                const nextFeeType = value === 'Flat Fee' ? 'Flat' : 'Percentage';
                if (nextFeeType !== feeType) setPendingFeeType(nextFeeType);
              }}
            />
            {pendingFeeType && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 10,
                  background: 'var(--tx-fill)',
                }}>
                <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.45 }}>
                  Switch to {pendingFeeType === 'Flat' ? 'Flat Fee' : 'Percentage'}? The current fee
                  value will be removed.
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => setPendingFeeType(null)}
                    style={{
                      ...btnPrimary,
                      flex: 1,
                      height: 32,
                      background: C.input,
                      color: C.muted,
                    }}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFeeType(pendingFeeType)}
                    style={{ ...btnPrimary, flex: 1, height: 32 }}>
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </div>
          <div>
            <div style={sLabel}>
              {feeType === 'Flat' ? 'Flat Fee Override' : 'Commission Rate'}{' '}
              {feeType === 'Flat' && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 400,
                    color: C.subtle,
                    textTransform: 'none',
                    letterSpacing: 0,
                  }}>
                  (blank = auto)
                </span>
              )}
            </div>
            {feeType === 'Flat' ? (
              <input
                aria-label="Flat fee override"
                value={feeOverride}
                onChange={(e) => setFeeOverride(formatCurrencyInput(e.target.value))}
                inputMode="numeric"
                placeholder={`Auto: ${fmtMoney(autoFee(deal.periodType))}`}
                style={{ ...inputStyle, height: 38, width: '100%', boxSizing: 'border-box' }}
              />
            ) : (
              <div style={{ position: 'relative' }}>
                <input
                  aria-label="Commission rate"
                  type="text"
                  inputMode="decimal"
                  value={commissionPercent}
                  onChange={(e) => setCommissionPercent(e.target.value)}
                  style={{
                    ...inputStyle,
                    height: 38,
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 400,
                    color: C.text,
                  }}
                />
                <span
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: C.subtle,
                    fontSize: 13,
                  }}>
                  %
                </span>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--tx-line)',
            display: 'flex',
            gap: 10,
          }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              height: 44,
              borderRadius: '12px',
              background: 'var(--tx-fill)',
              border: 0,
              color: C.muted,
              fontFamily: font,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={
              !!pendingFeeType ||
              (feeType === 'Percentage' && !isValidCommissionPercent(commissionPercent))
            }
            style={{
              flex: 2,
              height: 44,
              borderRadius: '12px',
              background: C.lime,
              border: 'none',
              color: 'var(--tx-on-lime)',
              fontFamily: font,
              fontSize: 13,
              fontWeight: 600,
              cursor:
                pendingFeeType ||
                (feeType === 'Percentage' && !isValidCommissionPercent(commissionPercent))
                  ? 'not-allowed'
                  : 'pointer',
              opacity:
                pendingFeeType ||
                (feeType === 'Percentage' && !isValidCommissionPercent(commissionPercent))
                  ? 0.5
                  : 1,
            }}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function GroupRow({
  group,
  isOpen,
  onToggle,
  isLast,
  onDealUpdate,
  onEdit,
  onArchive,
  pendingRequests,
  onApproveRequest,
  onRejectRequest,
  isResolvingRequestId,
}: {
  group: Group;
  isOpen: boolean;
  onToggle: () => void;
  isLast?: boolean;
  onDealUpdate?: (dealId: string, update: Partial<Deal>) => void;
  onEdit?: () => void;
  onArchive?: () => void;
  pendingRequests?: PendingPropertyRequest[];
  onApproveRequest?: (requestId: string) => void;
  onRejectRequest?: (requestId: string) => void;
  isResolvingRequestId?: string | null;
}) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [tab, setTab] = useState('deals');
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [ownershipRequest, setOwnershipRequest] = useState<PendingPropertyRequest | null>(null);
  const { openChatWithGroup } = useDashboardLayout();
  const { user } = useAuthContext();
  const isPrimaryAdvisor = group.advisorId === user?.id;
  const { users: allUsers } = useAdminUsers();
  const advisorOptions: AdvisorOption[] = useMemo(
    () =>
      (allUsers ?? [])
        .filter((u) => u.platformRole === 'Advisor')
        .map((u) => ({ userId: u.userId, name: u.name, email: u.email })),
    [allUsers],
  );
  const owner = primaryOwner(group.members);
  const totalFee = calculateRevenueForDeals(group.deals);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuOpen((p) => !p);
  };

  const menuPortal = menuOpen
    ? createPortal(
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 1299 }}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen(false);
            }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: menuPos.top,
              right: menuPos.right,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: '12px',
              padding: '4px 0',
              zIndex: 1300,
              minWidth: 160,
              boxShadow: 'var(--tx-shadow)',
            }}>
            <button
              onClick={() => {
                setMenuOpen(false);
                onEdit?.();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '9px 16px',
                background: 'transparent',
                border: 'none',
                color: C.text,
                fontFamily: font,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
              }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit group
            </button>
            <button
              onClick={() => {
                setMenuOpen(false);
                onArchive?.();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '9px 16px',
                background: 'transparent',
                border: 'none',
                color: group.status === 'archived' ? C.lime : C.muted,
                fontFamily: font,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'left',
              }}>
              {group.status === 'archived' ? (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="21 8 21 21 3 21 3 8" />
                  <rect x="1" y="3" width="22" height="5" />
                  <polyline points="10 13 12 11 14 13" />
                  <line x1="12" y1="11" x2="12" y2="17" />
                </svg>
              ) : (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="21 8 21 21 3 21 3 8" />
                  <rect x="1" y="3" width="22" height="5" />
                  <line x1="10" y1="12" x2="14" y2="12" />
                </svg>
              )}
              {group.status === 'archived' ? 'Restore to active' : 'Archive group'}
            </button>
          </div>
        </>,
        document.body,
      )
    : null;

  // Collapsed, a client is a flat row on the card. Open on a computer, the whole client (its row, tabs, deals and
  // properties) lifts into one raised box so it reads as the item in focus, everything inside it flat. The box reaches
  // 12px past the columns (the list's clip edge allows it), so they stay on the header's
  const openBox: React.CSSProperties = {
    margin: '10px -12px',
    padding: '0 12px 12px',
    borderRadius: '16px',
    background: 'var(--tx-glass-card)',
    backgroundImage: 'var(--tx-surface-light)',
    boxShadow: 'var(--tx-shadow-sm)',
  };
  return (
    <div
      style={
        isOpen && !isMobile
          ? openBox
          : isLast
            ? {}
            : { ...insetDivider, paddingBottom: 4 }
      }>
      {isMobile ? (
        <div
          onClick={onToggle}
          className={`heq-group-row${isOpen ? ' open' : ''}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '10px 0',
            cursor: 'pointer',
            marginTop: 10,
          }}>
          {/* Top row: chevron + [name / email col] + ⋮ */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div
              style={{
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: C.subtle,
                flexShrink: 0,
                marginTop: 3,
              }}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* Name + fee + ⋮ all on same line */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: C.text,
                    fontFamily: font,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}>
                  {group.holdingName}
                  {group.createdAt > Date.now() / 1000 - 7 * 86400 && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--tx-on-lime)',
                        background: 'var(--tx-lime)',
                        borderRadius: 5,
                        padding: '2px 6px',
                      }}>
                      New
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: C.text,
                    fontFamily: font,
                    flexShrink: 0,
                  }}>
                  {fmtMoney(totalFee)}
                </div>
                <button
                  ref={menuBtnRef}
                  onClick={openMenu}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    flexShrink: 0,
                    background: 'transparent',
                    border: 'none',
                    color: menuOpen ? C.text : C.subtle,
                    cursor: 'pointer',
                  }}>
                  <svg width="24" height="24" viewBox="8 1 8 22" fill="currentColor">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                  </svg>
                </button>
              </div>
              <div style={{ fontSize: 11, color: C.subtle, fontFamily: font }}>
                {owner?.email || owner?.name}
              </div>
            </div>
            {menuPortal}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 20 }}>
            {(() => {
              const allListings = group.deals.flatMap((d) => d.listings ?? []);
              if (allListings.length > 0) {
                return allListings.map((l) => (
                  <ListingStatusDot key={l.id} status={l.status} showTooltip />
                ));
              }
              return null;
            })()}
          </div>
        </div>
      ) : (
        <div
          onClick={onToggle}
          className={`heq-group-row${isOpen ? ' open' : ''}`}
          style={{
            display: 'grid',
            gridTemplateColumns: MG,
            // ~68px rows: between the old 86 and Properties' ~52
            padding: '12px 0',
            alignItems: 'center',
            cursor: 'pointer',
            gap: 25,
            marginTop: 4,
          }}>
          <div
            style={{
              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              transformOrigin: 'center',
              color: C.subtle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ClientAvatar name={owner?.name ?? ''} size={36} photoUrl={owner?.photoUrl} />
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: C.text,
                    fontFamily: font,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {group.holdingName}
                  {group.createdAt > Date.now() / 1000 - 7 * 86400 && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--tx-on-lime)',
                        background: 'var(--tx-lime)',
                        borderRadius: 5,
                        padding: '2px 6px',
                      }}>
                      New
                    </span>
                  )}
                </div>
                {pendingRequests && pendingRequests.length > 0 && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 18,
                      height: 18,
                      borderRadius: '6px',
                      backgroundColor: C.lime,
                      color: 'var(--tx-text)',
                      fontSize: 10,
                      fontWeight: 600,
                      fontFamily: font,
                      padding: '0 5px',
                      flexShrink: 0,
                    }}>
                    {pendingRequests.length}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: 'var(--tx-muted)',
                  fontFamily: font,
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {owner?.email || owner?.name}
              </div>
            </div>
          </div>

          <div />

          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(() => {
              const allListings = group.deals.flatMap((d) => d.listings ?? []);
              if (allListings.length > 0) {
                return allListings.map((l) => (
                  <ListingStatusDot key={l.id} status={l.status} showTooltip />
                ));
              }
              return <span style={{ color: C.subtle, fontSize: 12 }}>—</span>;
            })()}
          </div>

          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: C.text, fontFamily: font, fontVariantNumeric: 'tabular-nums' }}>
              {group.deals.length}
            </div>
          </div>

          {(() => {
            const totalOffers = group.deals.reduce((sum, d) => sum + autoOffers(d.periodType), 0);
            const usedOffers = group.deals.reduce((sum, d) => sum + dealEffectiveOffersUsed(d), 0);
            return (
              <div>
                <div style={{ fontSize: 12, fontFamily: font, marginBottom: 5 }}>
                  <span
                    style={{
                      color: usedOffers >= totalOffers ? L.critical : C.text,
                      fontWeight: 500,
                    }}>
                    {usedOffers}
                  </span>
                  <span style={{ color: C.subtle }}> / {totalOffers}</span>
                </div>
                {/* Fixed 6-pill grid so a 12-offer group renders as a
                    balanced 2x6 instead of an orphan-prone wrapped flex row. */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 8px)',
                    gap: 3,
                  }}>
                  {Array.from({ length: totalOffers }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        width: 8,
                        height: 4,
                        borderRadius: 2,
                        background: i < usedOffers ? L.moderate : 'var(--tx-fill-2)',
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          <div style={{ display: 'flex', alignItems: 'center' }}>
            <GroupExpiryDonut deals={group.deals} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: C.text, fontFamily: font, fontVariantNumeric: 'tabular-nums' }}>
              {fmtMoney(totalFee)}
            </span>
            <button
              ref={menuBtnRef}
              onClick={openMenu}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 6,
                marginLeft: 10,
                background: 'transparent',
                border: 'none',
                color: menuOpen ? C.text : C.subtle,
                cursor: 'pointer',
                flexShrink: 0,
              }}>
              <svg width="24" height="24" viewBox="8 1 8 22" fill="currentColor">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
            {menuPortal}
          </div>
        </div>
      )}

      {isOpen && (
        <div
          style={{
            paddingBottom: 20,
            paddingLeft: 0,
            paddingRight: 0,
          }}>
          {/* ─── Pending Property Requests ─────────────────────── */}
          {pendingRequests && pendingRequests.length > 0 && (
            <div
              style={{
                marginLeft: isMobile ? 0 : 53,
                marginBottom: 16,
                padding: '12px 14px',
                borderRadius: 10,
                backgroundColor: 'var(--tx-fill)',
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  color: C.muted,
                  fontFamily: font,
                  marginBottom: 6,
                }}>
                {/* one lime tick is the only accent: something is waiting on you */}
                <span style={{ width: 2, height: 12, borderRadius: 1, background: C.lime }} />
                Property requests ({pendingRequests.length})
              </div>
              {pendingRequests.map((req, reqIndex) => {
                const isResolving = isResolvingRequestId === req.id;
                const isOwnershipClaim = req.requestType === 'ownership_claim';
                return (
                  <div
                    key={req.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '8px 0',
                      borderBottom:
                        reqIndex < pendingRequests.length - 1 ? '1px solid var(--tx-line)' : 'none',
                    }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.text,
                          fontFamily: font,
                          overflow: 'hidden',
                        }}>
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                          {req.fullAddress}
                        </span>
                        {isOwnershipClaim && (
                          <span
                            style={{
                              flexShrink: 0,
                              fontSize: 11,
                              fontWeight: 500,
                              color: C.muted,
                              marginLeft: 4,
                            }}>
                            Ownership claim
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, fontFamily: font, marginTop: 2 }}>
                        {isOwnershipClaim
                          ? 'Wants to claim ownership · Requested by'
                          : 'Requested by'}{' '}
                        {req.buyerName} ·{' '}
                        {new Date(req.createdAt * 1000).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <DsButton
                        variant="ghost"
                        size="sm"
                        disabled={isResolving}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRejectRequest?.(req.id);
                        }}>
                        Dismiss
                      </DsButton>
                      <DsButton
                        variant="primary"
                        size="sm"
                        disabled={isResolving}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isOwnershipClaim) setOwnershipRequest(req);
                          else onApproveRequest?.(req.id);
                        }}>
                        {isResolving
                          ? isOwnershipClaim
                            ? 'Granting...'
                            : 'Adding...'
                          : isOwnershipClaim
                          ? 'Grant Ownership'
                          : 'Add Property'}
                      </DsButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 0,
              paddingLeft: isMobile ? 0 : 53,
              paddingBottom: 20,
              backgroundColor: 'transparent',
            }}>
            {['deals', 'members'].map((t) => (
              <button
                key={t}
                onClick={(e) => {
                  e.stopPropagation();
                  setTab(t);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  color: tab === t ? C.text : C.subtle,
                  fontFamily: font,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '10px 16px 10px 0',
                  cursor: 'pointer',
                  transition: 'color 0.15s',
                }}>
                <span
                  style={{
                    borderBottom: tab === t ? `2px solid ${C.text}` : '2px solid transparent',
                    paddingBottom: '4px',
                  }}>
                  {t === 'deals'
                    ? `Deals (${group.deals.length})`
                    : `Members (${group.members.length})`}
                </span>
              </button>
            ))}
          </div>

          {tab === 'deals' && (
            <>
              {isMobile ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '6px 0 4px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.muted,
                    fontFamily: font,
                  }}>
                  <span>Deal</span>
                  <span>Fee</span>
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: MG,
                    padding: '8px 0',
                    background: 'transparent',
                    gap: 25,
                  }}>
                  {['', 'Deal', 'Co-Advisor', 'Status', 'Period', 'Offers', 'Expires', 'Fee'].map(
                    (h, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.muted,
                          fontFamily: font,
                        }}>
                        {h}
                      </div>
                    ),
                  )}
                </div>
              )}
              {group.deals.map((d) => (
                <DealRow
                  key={d.id}
                  deal={d}
                  onUpdate={(update) => onDealUpdate?.(d.id, update)}
                  onEdit={() => setEditingDeal(d)}
                  advisors={advisorOptions}
                  primaryAdvisorId={group.advisorId}
                  members={group.members}
                  lockedPropertyIds={group.lockedPropertyIds ?? []}
                  groupIsOpen={isOpen}
                />
              ))}
              {isPrimaryAdvisor && (
                <div style={{ paddingLeft: isMobile ? 0 : 53, paddingTop: 8 }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit?.();
                    }}
                    style={{
                      background: 'var(--tx-fill)',
                      color: 'var(--tx-muted)',
                      border: 'none',
                      borderRadius: 8,
                      padding: '5px 12px',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: font,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}>
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#bbb7af"
                      strokeWidth="2.5"
                      strokeLinecap="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Deal
                  </button>
                </div>
              )}
            </>
          )}

          {tab === 'members' && (
            <>
              {isMobile ? (
                <div
                  style={{
                    padding: '6px 0 4px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.muted,
                    fontFamily: font,
                  }}>
                  Member
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: MG,
                    padding: '8px 0',
                    background: 'transparent',
                    gap: 25,
                  }}>
                  {['', 'Member', '', 'Phone', 'Role', 'Contact', '', ''].map((h, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.muted,
                        fontFamily: font,
                      }}>
                      {h}
                    </div>
                  ))}
                </div>
              )}
              {group.members.map((m) => {
                const onChatClick = () => openChatWithGroup(group.id);
                return <MemberRow key={m.id} member={m} onChatClick={onChatClick} />;
              })}
              <div style={{ paddingLeft: isMobile ? 0 : 53, paddingTop: 8 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit?.();
                  }}
                  style={{
                    background: 'var(--tx-fill)',
                    color: 'var(--tx-muted)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '5px 12px',
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}>
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#bbb7af"
                    strokeWidth="2.5"
                    strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Member
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {editingDeal && (
        <EditDealDrawer
          deal={editingDeal}
          onClose={() => setEditingDeal(null)}
          onSave={(updated) => {
            onDealUpdate?.(editingDeal.id, updated);
            setEditingDeal(null);
          }}
        />
      )}
      <Dialog
        open={!!ownershipRequest}
        onClose={() => setOwnershipRequest(null)}
        width={520}
        title="Review ownership claim"
        subtitle="Confirm the request details before changing this home's recorded owner."
        containerProps={{ gap: 0, p: '25px' }}>
        {ownershipRequest && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: '14px',
              }}>
              <Box
                sx={{
                  px: '9px',
                  py: '4px',
                  borderRadius: '5px',
                  bgcolor: 'var(--tx-fill-2)',
                  color: 'var(--tx-text)',
                  fontFamily: font,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                }}>
                OWNERSHIP REQUEST
              </Box>
              <Typography sx={{ fontFamily: font, fontSize: 11, color: C.subtle }}>
                Submitted{' '}
                {new Date(ownershipRequest.createdAt * 1000).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Box
                sx={{
                  p: '14px',
                  borderRadius: '12px',
                  bgcolor: 'var(--tx-fill)',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <Typography sx={{ fontFamily: font, fontSize: 10, color: C.subtle, mb: '12px' }}>
                  CLAIMANT
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: '50%',
                      bgcolor: 'var(--tx-fill-2)',
                      color: C.muted,
                      fontWeight: 600,
                    }}>
                    {ownershipRequest.buyerName.charAt(0).toUpperCase()}
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: font, fontSize: 14, fontWeight: 600 }}>
                      {ownershipRequest.buyerName}
                    </Typography>
                    <Typography sx={{ fontFamily: font, fontSize: 11, color: C.muted }}>
                      Hauser member
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ height: 1, bgcolor: 'rgba(255,255,255,0.12)', my: '12px' }} />
                <Typography sx={{ mt: 'auto', fontFamily: font, fontSize: 11, color: C.muted }}>
                  Claim submitted {new Date(ownershipRequest.createdAt * 1000).toLocaleDateString()}
                </Typography>
              </Box>
              <PropertyAddressMapCard
                compact
                address={ownershipRequest.address}
                locality={`${ownershipRequest.city}, ${ownershipRequest.state} ${ownershipRequest.zipCode}`}
              />
            </Box>
            <Box
              sx={{
                mt: '12px',
                p: '10px 12px',
                borderRadius: '9px',
                bgcolor: 'var(--tx-fill)',
              }}>
              <Typography sx={{ fontFamily: font, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                Granting this request makes {ownershipRequest.buyerName} the recorded owner.
                Existing advisor access remains unchanged.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', mt: '20px' }}>
              <Box
                component="button"
                onClick={() => {
                  onRejectRequest?.(ownershipRequest.id);
                  setOwnershipRequest(null);
                }}
                sx={{
                  height: 40,
                  px: 3,
                  borderRadius: '10px',
                  border: 'none',
                  background: 'var(--tx-fill)',
                  bgcolor: 'transparent',
                  color: C.muted,
                  fontFamily: font,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}>
                Reject
              </Box>
              <Box
                component="button"
                onClick={() => {
                  onApproveRequest?.(ownershipRequest.id);
                  setOwnershipRequest(null);
                }}
                sx={{
                  height: 40,
                  px: 3,
                  borderRadius: '10px',
                  border: 'none',
                  bgcolor: C.lime,
                  color: 'var(--tx-text)',
                  fontFamily: font,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}>
                Grant ownership
              </Box>
            </Box>
          </>
        )}
      </Dialog>
    </div>
  );
}

// ─── Add Client Dialog ────────────────────────────────────────────────────────
const CLIENT_TYPES = ['Individual', 'LLC', 'Trust', 'Partnership'] as const;
const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

function StateSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen((o) => !o);
  };

  return (
    <div ref={triggerRef} style={{ position: 'relative' }}>
      {/* the trigger reads as a ds TextInput well: tonal fill, radius 12, 40px, fill-2 on hover / open */}
      <Box
        component="button"
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="State"
        onClick={handleOpen}
        sx={{
          width: '100%',
          height: 40,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '12px',
          border: 0,
          borderRadius: '12px',
          background: open ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          transition: 'background-color 0.15s ease',
          '&:hover': { background: 'var(--tx-fill-2)' },
          '&:focus-visible': { outline: '2px solid var(--tx-focus)', outlineOffset: 2 },
        }}>
        <Box
          component="span"
          sx={{
            flex: 1,
            fontSize: 'var(--tx-field-size)',
            fontWeight: value ? 600 : 500,
            color: value ? 'var(--tx-text)' : 'var(--tx-faint)',
          }}>
          {value || 'State'}
        </Box>
        <Box
          component="svg"
          viewBox="0 0 24 24"
          aria-hidden
          sx={{
            width: 14,
            height: 14,
            fill: 'none',
            stroke: 'var(--tx-faint)',
            strokeWidth: 1.6,
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}>
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </Box>
      </Box>
      {open &&
        createPortal(
          <Box
            ref={menuRef}
            role="listbox"
            aria-label="State"
            sx={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: Math.max(menuPos.width, 96),
              zIndex: 1400,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '12px',
              padding: '4px',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              boxShadow: 'var(--tx-shadow)',
              maxHeight: 220,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--tx-fill-3)',
                borderRadius: 4,
              },
            }}>
            {US_STATES.map((s) => (
              <Box
                key={s}
                role="option"
                aria-selected={s === value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(s);
                  setOpen(false);
                }}
                sx={{
                  px: '10px',
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '8px',
                  fontFamily: font,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.1s',
                  color: s === value ? 'var(--tx-text)' : 'var(--tx-muted)',
                  background: s === value ? 'var(--tx-fill-2)' : 'transparent',
                  '&:hover': { background: 'var(--tx-fill-2)', color: 'var(--tx-text)' },
                }}>
                {s}
              </Box>
            ))}
          </Box>,
          document.body,
        )}
    </div>
  );
}
type ClientType = (typeof CLIENT_TYPES)[number];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: font,
    fontSize: 13,
    color: 'var(--tx-text)',
    background: 'var(--tx-fill)',
    backgroundImage: 'none',
    borderRadius: '10px',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(187,183,175,0.5)', borderWidth: 1 },
    '&::before': { display: 'none' },
    '&::after': { display: 'none' },
  },
  '& .MuiInputBase-input': { color: 'var(--tx-text)' },
  '& .MuiInputBase-input::placeholder': { color: '#737373', opacity: 1 },
};

// The Add client dialog's MUI fields (address autocomplete, date picker) dressed as a ds TextInput: a tonal well,
// radius 12, no outline, fill-2 on hover and focus, 13/600 type with a faint 500 placeholder
const dsFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontFamily: 'inherit',
    fontSize: 'var(--tx-field-size)',
    fontWeight: 600,
    color: 'var(--tx-text)',
    background: 'var(--tx-fill)',
    backgroundImage: 'none',
    borderRadius: '12px',
    transition: 'background-color 0.15s ease',
    '& fieldset': { border: 0 },
    '&:hover, &.Mui-focused': { background: 'var(--tx-fill-2)' },
    '&::before': { display: 'none' },
    '&::after': { display: 'none' },
  },
  '& .MuiInputBase-input': { color: 'var(--tx-text)' },
  '& .MuiInputBase-input::placeholder': { color: 'var(--tx-faint)', fontWeight: 500, opacity: 1 },
};

function AddressAutocomplete({
  address,
  onSelect,
  onAddressChange,
}: {
  address: string;
  onSelect: (a: { address: string; city: string; state: string; zip: string }) => void;
  onAddressChange: (val: string) => void;
}) {
  const placesLibAC = useMapsLibrary('places');
  const mapsLibAC = useMapsLibrary('maps');
  const [geocoderAC, setGeocoderAC] = useState<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (mapsLibAC) setGeocoderAC(new google.maps.Geocoder());
  }, [mapsLibAC]);

  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selected, setSelected] = useState<google.maps.places.AutocompletePrediction | null>(null);

  const streetFromSelected = selected?.structured_formatting?.main_text ?? null;
  const displayValue = streetFromSelected ?? (address && !query ? address : query);

  useEffect(() => {
    if (!selected || !geocoderAC) return;
    let cancelled = false;
    geocoderAC.geocode({ placeId: selected.place_id }, (results, status) => {
      if (cancelled || status !== google.maps.GeocoderStatus.OK || !results?.[0]) return;
      const components = parseGoogleAddressComponents(results[0].address_components);
      const street = [components.streetNumber, components.street].filter(Boolean).join(' ');
      onSelect({
        address: street,
        city: components.city ?? '',
        state: components.state ?? '',
        zip: components.zip ?? '',
      });
    });
    return () => {
      cancelled = true;
    };
  }, [selected, geocoderAC]);

  useEffect(() => {
    if (!query || query.length < 3 || selected) {
      if (!query || query.length < 3) setOptions([]);
      return;
    }
    if (!placesLibAC) return;

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const suggestions = await fetchAddressSuggestions(query, placesLibAC);
        setOptions(suggestions);
      } catch {
        setOptions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [query, selected, placesLibAC]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.description ?? '')}
      filterOptions={(x) => x}
      inputValue={displayValue}
      onInputChange={(_e, val, reason) => {
        if (reason === 'input') {
          setSelected(null);
          setQuery(val);
          onAddressChange(val);
        } else if (reason === 'clear') {
          setSelected(null);
          setQuery('');
          setOptions([]);
          onSelect({ address: '', city: '', state: '', zip: '' });
        }
      }}
      onChange={(_e, val) => {
        if (val === null) {
          setSelected(null);
          setQuery('');
          setOptions([]);
          onSelect({ address: '', city: '', state: '', zip: '' });
          return;
        }
        if (typeof val === 'string') return;
        setSelected(val);
        setQuery('');
      }}
      loading={isSearching}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Start typing an address…"
          variant="outlined"
          sx={{
            ...dsFieldSx,
            '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': { paddingLeft: '0px !important' },
            '& .MuiAutocomplete-input': { paddingLeft: '12px !important' },
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <Box
                  component="span"
                  aria-hidden
                  sx={{ display: 'flex', color: 'var(--tx-faint)', ml: '12px', mr: '-4px' }}>
                  <Icon name="pin" size={16} stroke={1.6} />
                </Box>
              ),
              endAdornment: (
                <>
                  {isSearching ? <CircularProgress size={14} sx={{ color: 'var(--tx-faint)' }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            background: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '12px',
            mt: '4px',
            p: '4px',
            '& .MuiAutocomplete-listbox': { p: 0 },
            '& .MuiAutocomplete-option': {
              fontFamily: font,
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--tx-text)',
              borderRadius: '8px',
              minHeight: 36,
              '&:hover, &.Mui-focused': { bgcolor: 'var(--tx-fill-2) !important' },
              '&[aria-selected="true"]': { bgcolor: 'var(--tx-fill-2) !important' },
            },
            '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
              fontFamily: font,
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--tx-faint)',
            },
          },
        },
      }}
      noOptionsText={query.length >= 3 ? 'No addresses found' : 'Type to search…'}
    />
  );
}

// a section's name inside the Add client dialog: 12/600 faint
function SectionLabel({ children }: { children: string }) {
  return (
    <Typography
      sx={{
        fontFamily: font,
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--tx-faint)',
        mb: '14px',
      }}>
      {children}
    </Typography>
  );
}

// the quiet "add" slot, in the records sleeve's language: a dashed sheet that is itself the click target, a tonal
// plus circle (never lime), lifting a touch on hover. row = a slim strip for a list (members), else a taller tile
function AddTile({
  label,
  sub,
  onClick,
  locked,
  title,
  row,
}: {
  label: string;
  sub?: string;
  onClick: () => void;
  locked?: boolean;
  title?: string;
  row?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={locked ? undefined : onClick}
      disabled={locked}
      title={title}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: row ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: row ? '10px' : '8px',
        minHeight: row ? 52 : 132,
        p: row ? '8px 14px' : '20px',
        borderRadius: '14px',
        border: `1.5px dashed rgba(var(--tx-ink-rgb), ${locked ? 0.12 : 0.24})`,
        background: 'transparent',
        color: locked ? 'var(--tx-faint)' : 'var(--tx-muted)',
        fontFamily: font,
        cursor: locked ? 'default' : 'pointer',
        transition:
          'transform 0.45s cubic-bezier(0.32, 0.72, 0, 1), border-color 0.15s ease, color 0.15s ease',
        '& .hx-add-plus': {
          width: row ? 28 : 36,
          height: row ? 28 : 36,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
          background: 'var(--tx-fill)',
          color: locked ? 'var(--tx-faint)' : 'var(--tx-text)',
          transition: 'background 0.15s ease',
        },
        ...(locked
          ? {}
          : {
              '&:hover, &:focus-visible': {
                transform: 'translateY(-2px)',
                borderColor: 'rgba(var(--tx-ink-rgb), 0.45)',
                color: 'var(--tx-text)',
              },
              '&:hover .hx-add-plus': { background: 'var(--tx-fill-2)' },
            }),
        '&:focus-visible': { outline: '2px solid var(--tx-focus)', outlineOffset: 3 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}>
      <span className="hx-add-plus" aria-hidden>
        <Icon name={locked ? 'lock' : 'plus'} size={row ? 14 : 16} stroke={1.8} />
      </span>
      <Box
        component="span"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: row ? 'flex-start' : 'center',
          gap: '2px',
        }}>
        <Box component="span" sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em' }}>
          {label}
        </Box>
        {sub && (
          <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)' }}>
            {sub}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function PillToggle({
  options,
  value,
  onChange,
}: {
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {options.map((opt) => (
        <Box
          key={opt}
          onClick={() => onChange(opt)}
          sx={{
            px: '10px',
            py: '4px',
            borderRadius: '6px',
            fontSize: 11,
            fontFamily: font,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.12s',
            userSelect: 'none',
            background: value === opt ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
            color: value === opt ? 'var(--tx-text)' : 'var(--tx-muted)',
            border: 0,
            '&:hover': { background: value === opt ? '#bbb7af' : 'rgba(255,255,255,0.1)' },
          }}>
          {opt}
        </Box>
      ))}
    </Box>
  );
}

function normalizePhone(val: string): string {
  const digits = val.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function AddClientDialog({
  open,
  onClose,
  onAdd,
  editGroup,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: () => void;
  editGroup?: Group;
  onSave?: (g: Group) => void;
}) {
  const isEdit = !!editGroup;
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { user } = useAuthContext();
  const isCoAdvisorOnly =
    !!editGroup &&
    editGroup.advisorId !== user?.id &&
    (editGroup.deals ?? []).some((d) => (d.coAdvisorIds ?? []).includes(user?.id ?? ''));
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const createGroupMutation = useCreateClientGroup();
  const deleteGroupMutation = useDeleteClientGroup();
  const createDealMutation = useCreateDeal();
  const createMemberMutation = useCreateMember();
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [clientType, setClientType] = useState<ClientType>('Individual');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [unit, setUnit] = useState('');
  const [city, setCity] = useState('');
  const [stateVal, setStateVal] = useState('');
  const [zip, setZip] = useState('');
  const [addDeal, setAddDeal] = useState(!editGroup);
  const [dealName, setDealName] = useState('');
  const [dealStatus, setDealStatus] = useState('Search');
  const [periodType, setPeriodType] = useState('Initial');
  const [engagementStart, setEngagementStart] = useState('');
  const [feeType, setFeeType] = useState<DealFeeType>('Flat');
  const [feeOverride, setFeeOverride] = useState<number | null>(null);
  const [commissionPercent, setCommissionPercent] = useState('2.5');
  const [notes, setNotes] = useState('');
  const [extraMembers, setExtraMembers] = useState<Member[]>([]);

  const addExtraMember = () =>
    setExtraMembers((prev) => [
      ...prev,
      {
        id: `m-${Date.now()}`,
        groupId: '',
        name: '',
        email: '',
        phone: '',
        role: 'Co-Buyer',
        createdAt: 0,
        updatedAt: 0,
      },
    ]);
  const removeExtraMember = (id: string) =>
    setExtraMembers((prev) => prev.filter((m) => m.id !== id));
  const updateExtraMember = (id: string, field: keyof Member, val: string) =>
    setExtraMembers((prev) => prev.map((m) => (m.id === id ? { ...m, [field]: val } : m)));

  const reset = () => {
    setGroupName('');
    setClientType('Individual');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setUnit('');
    setCity('');
    setStateVal('');
    setZip('');
    setAddDeal(false);
    setDealName('');
    setDealStatus('Search');
    setPeriodType('Initial');
    setEngagementStart('');
    setFeeType('Flat');
    setFeeOverride(null);
    setCommissionPercent('2.5');
    setNotes('');
    setExtraMembers([]);
    setApiError('');
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!open) return;
    if (editGroup) {
      // Find primary by role — array order is not guaranteed, so don't rely on index 0
      const primary =
        editGroup.members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') ??
        editGroup.members[0];
      const parts = primary?.name?.trim().split(' ') ?? [];
      setGroupName(editGroup.holdingName);
      setClientType((editGroup.clientType as ClientType) ?? 'Individual');
      setFirstName(parts[0] ?? '');
      setLastName(parts.slice(1).join(' '));
      setEmail(primary?.email ?? '');
      setPhone(normalizePhone(primary?.phone ?? ''));
      setAddress(primary?.address ?? '');
      setUnit('');
      setCity(primary?.city ?? '');
      setStateVal(primary?.state ?? '');
      setZip(primary?.zip ?? '');
      setNotes(editGroup.notes ?? '');
      setAddDeal(false);
      setExtraMembers(editGroup.members.filter((m) => m.id !== primary?.id));
    } else {
      reset();
    }
  }, [open, editGroup?.id]);

  const handleClose = () => {
    if (!isEdit) reset();
    onClose();
  };

  const canSubmit =
    groupName.trim() &&
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    phone.trim() &&
    address.trim() &&
    city.trim() &&
    stateVal &&
    zip.trim() &&
    (!addDeal ||
      !dealName.trim() ||
      feeType === 'Flat' ||
      isValidCommissionPercent(commissionPercent));

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;
    setApiError('');
    const primaryMember = {
      name: `${firstName.trim()} ${lastName.trim()}`,
      email: email.trim(),
      phone: phone.trim(),
      address: [address.trim(), unit.trim()].filter(Boolean).join(' '),
      city: city.trim(),
      state: stateVal,
      zip: zip.trim(),
    };
    if (isEdit && editGroup) {
      const updatedGroup: Group = {
        ...editGroup,
        holdingName: groupName.trim(),
        clientType,
        notes: notes.trim(),
        members: [
          {
            ...(editGroup.members.find((m) => m.role === 'Buyer' || m.role === 'Primary Owner') ?? {
              id: crypto.randomUUID(),
              groupId: editGroup.id,
              role: 'Buyer',
              createdAt: 0,
              updatedAt: 0,
            }),
            ...primaryMember,
          },
          ...extraMembers,
        ],
      };
      onSave?.(updatedGroup);
      if (addDeal && dealName.trim()) {
        setIsSubmitting(true);
        try {
          await createDealMutation.mutateAsync({
            groupId: editGroup.id,
            data: {
              name: dealName.trim(),
              status: dealStatus,
              periodType,
              engagementStart: engagementStart || new Date().toISOString().split('T')[0],
              ...buildDealFeePayload(feeType, feeOverride, commissionPercent),
            },
          });
        } catch {
          setApiError('Client saved, but failed to create deal. Please try again.');
          showAlert(t('deal.save_failed'), 'error');
          setIsSubmitting(false);
          return;
        }
        setIsSubmitting(false);
      }
      showAlert(t('client.saved'), 'success');
      handleClose();
    } else {
      setIsSubmitting(true);
      let createdGroupId: string | null = null;
      try {
        // 1. Create the group
        const { group: newGroup } = await createGroupMutation.mutateAsync({
          holdingName: groupName.trim(),
          clientType,
          notes: notes.trim() || undefined,
        });
        createdGroupId = newGroup.id;

        // 2. Create primary member
        await createMemberMutation.mutateAsync({
          groupId: newGroup.id,
          data: {
            name: primaryMember.name,
            email: primaryMember.email,
            phone: primaryMember.phone,
            role: 'Buyer',
            address: primaryMember.address,
            city: primaryMember.city,
            state: primaryMember.state,
            zip: primaryMember.zip,
          },
        });

        // 3. Create extra members
        await Promise.all(
          extraMembers.map((m) =>
            createMemberMutation.mutateAsync({
              groupId: newGroup.id,
              data: {
                name: m.name,
                email: m.email,
                phone: m.phone,
                role: m.role,
                address: m.address,
                city: m.city,
                state: m.state,
                zip: m.zip,
              },
            }),
          ),
        );

        // 4. Create deal if provided
        if (addDeal && dealName.trim()) {
          await createDealMutation.mutateAsync({
            groupId: newGroup.id,
            data: {
              name: dealName.trim(),
              status: dealStatus,
              periodType,
              engagementStart: engagementStart || new Date().toISOString().split('T')[0],
              ...buildDealFeePayload(feeType, feeOverride, commissionPercent),
            },
          });
        }

        onAdd();
        showAlert(t('client.added'), 'success');
        handleClose();
      } catch (_err: any) {
        // Compensating cleanup: if the group was created but a subsequent step failed,
        // archive it to avoid leaving an orphaned group with no members/deals.
        if (createdGroupId) {
          try {
            await deleteGroupMutation.mutateAsync(createdGroupId);
          } catch {
            // Swallow cleanup errors; primary failure is already reported below.
          }
        }
        setApiError('Failed to create client. Please try again.');
        showAlert(t('client.add_failed'), 'error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // sections part with space and a 1px hairline, never a boxed tile
  const divider = <DsDivider sx={{ my: '16px' }} />;
  const pickerPopperSx = {
    zIndex: 10001,
    '& .MuiPaper-root': {
      background: 'var(--tx-glass-panel)',
      backgroundImage: 'var(--tx-surface-light)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderRadius: '16px',
      boxShadow: 'var(--tx-shadow)',
    },
    '& .MuiPickersCalendarHeader-label': { fontFamily: font, fontWeight: 600, color: 'var(--tx-text)' },
    '& .MuiPickersArrowSwitcher-button': {
      color: 'var(--tx-muted)',
      '&:hover': { background: 'rgba(255,255,255,0.08)' },
    },
    '& .MuiDayCalendar-weekDayLabel': {
      fontFamily: font,
      fontSize: 11,
      color: 'var(--tx-faint)',
      fontWeight: 600,
    },
    '& .MuiPickersDay-root': {
      fontFamily: font,
      fontSize: 12,
      fontWeight: 600,
      color: 'var(--tx-muted)',
      background: 'transparent',
      borderRadius: '8px',
      '&:hover': { background: 'rgba(255,255,255,0.08)' },
      '&.Mui-selected': { background: '#c0ff02 !important', color: '#262626', fontWeight: 600 },
      '&.MuiPickersDay-today': { border: '1px solid rgba(192,255,2,0.4)', color: 'var(--tx-text)' },
    },
  };
  const pairCols = isMobile ? '1fr' : '1fr 1fr';
  const submitReady = !!canSubmit && !isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      width={isMobile ? undefined : 1200}
      containerProps={{ gap: 0, p: 0 }}
      slotProps={{
        paper: {
          sx: {
            // fits a 13" screen: never wider or taller than the view less the frame's 20px gutter; the body scrolls
            maxWidth: isMobile ? 'calc(100vw - 20px) !important' : 'min(1080px, calc(100vw - 40px)) !important',
            maxHeight: 'calc(100dvh - 40px)',
            overflowY: 'auto',
            ...(isMobile && { width: 'calc(100vw - 20px)', mx: '10px' }),
          },
        },
      }}>
      {/* Header — above both columns; the shell's close sits top right */}
      <Box
        sx={{
          px: isMobile ? '20px' : '28px',
          pt: isMobile ? '22px' : '20px',
          pb: isMobile ? '16px' : '14px',
          pr: '72px',
        }}>
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontFamily: font,
            fontSize: 17,
            fontWeight: 400,
            letterSpacing: '-0.015em',
            lineHeight: 1.3,
            color: 'var(--tx-text)',
            mb: '4px',
          }}>
          {isEdit ? 'Edit client' : 'Add a client'}
        </Typography>
        <Typography
          sx={{ fontFamily: font, fontSize: 13, fontWeight: 500, color: 'var(--tx-muted)', lineHeight: 1.5 }}>
          {isEdit
            ? 'Update client group and contact details.'
            : 'Create a new client group and assign a primary contact.'}
        </Typography>
      </Box>

      {/* Hairline under the header */}
      <DsDivider sx={{ mx: isMobile ? '20px' : '28px' }} />

      {/* Two-column layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          minHeight: 0,
          position: 'relative',
          '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)': { height: 40 },
        }}>
        {/* ── Left: Client group + Primary contact + Current address + Members ── */}
        <Box
          sx={{ p: isMobile ? '20px' : '28px', display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field label="Group name" htmlFor="add-client-group-name">
              <TextInput
                id="add-client-group-name"
                placeholder="e.g. Smith Family Trust, Johnson LLC"
                fullWidth
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </Field>
            <Field label="Type">
              <Segmented<ClientType>
                label="Client type"
                block
                size={isMobile ? 'sm' : 'md'}
                options={CLIENT_TYPES.map((ct) => [ct, ct] as [ClientType, string])}
                value={clientType}
                onChange={setClientType}
              />
            </Field>
          </Box>

          {divider}

          <SectionLabel>Primary contact</SectionLabel>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Field label="First name" htmlFor="add-client-first-name">
                <TextInput
                  id="add-client-first-name"
                  placeholder="First name"
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Field>
              <Field label="Last name" htmlFor="add-client-last-name">
                <TextInput
                  id="add-client-last-name"
                  placeholder="Last name"
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Field>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: pairCols, gap: '12px' }}>
              <Field label="Email" htmlFor="add-client-email">
                <TextInput
                  id="add-client-email"
                  type="email"
                  icon="mail"
                  placeholder="name@example.com"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <Field label="Phone" htmlFor="add-client-phone">
                <TextInput
                  id="add-client-phone"
                  type="tel"
                  icon="phone"
                  placeholder="(555) 555-5555"
                  fullWidth
                  value={phone}
                  onChange={(e) => setPhone(normalizePhone(e.target.value))}
                />
              </Field>
            </Box>
          </Box>

          {divider}

          <SectionLabel>Current address</SectionLabel>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
              gap: '12px 12px',
            }}>
            <Field label="Street address" sx={{ gridColumn: isMobile ? '1 / -1' : '1 / 4' }}>
              <AddressAutocomplete
                address={address}
                onAddressChange={setAddress}
                onSelect={(a) => {
                  setAddress(a.address);
                  setCity(a.city);
                  setStateVal(a.state);
                  setZip(a.zip);
                }}
              />
            </Field>
            <Field label="Unit" htmlFor="add-client-unit">
              <TextInput
                id="add-client-unit"
                placeholder="Apt #"
                fullWidth
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
            </Field>
            <Field label="City" htmlFor="add-client-city" sx={{ gridColumn: isMobile ? 'auto' : '1 / 3' }}>
              <TextInput
                id="add-client-city"
                placeholder="City"
                fullWidth
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Field>
            <Field label="State">
              <StateSelect value={stateVal} onChange={setStateVal} />
            </Field>
            <Field label="ZIP" htmlFor="add-client-zip">
              <TextInput
                id="add-client-zip"
                placeholder="ZIP"
                fullWidth
                inputProps={{ inputMode: 'numeric' }}
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </Field>
          </Box>

        </Box>

        {/* Vertical hairline — desktop only */}
        {!isMobile && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: '50%',
              top: '28px',
              bottom: '28px',
              width: '1px',
              background: 'var(--tx-line)',
            }}
          />
        )}
        {isMobile && <DsDivider sx={{ mx: '20px' }} />}

        {/* ── Right: Deal + Notes + foot ── */}
        <Box
          sx={{ p: isMobile ? '20px' : '28px', display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }}>
          <SectionLabel>Deal</SectionLabel>

          {isEdit && editGroup?.deals?.[0] && !addDeal ? (
            <>
              {/* Edit mode + existing deal → a read-only summary, then the quiet add slot */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: '16px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                  <Typography
                    sx={{
                      fontFamily: font,
                      fontSize: 15,
                      fontWeight: 600,
                      letterSpacing: '-0.015em',
                      color: 'var(--tx-text)',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {editGroup.deals[0].name}
                  </Typography>
                  <Tag
                    color={STATUS_COLOR[effectiveDealStatus(editGroup.deals[0])] || STATUS_COLOR.Search}>
                    {effectiveDealStatus(editGroup.deals[0])}
                  </Tag>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    fontFamily: font,
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--tx-muted)',
                  }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <Icon name="clock" size={14} stroke={1.6} style={{ color: 'var(--tx-faint)' }} />
                    {editGroup.deals[0].periodType}
                  </Box>
                  {editGroup.deals[0].engagementStart && (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Icon name="calendar" size={14} stroke={1.6} style={{ color: 'var(--tx-faint)' }} />
                      Started{' '}
                      {formatLocalDate(editGroup.deals[0].engagementStart, {
                        month: 'short',
                        year: 'numeric',
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
              <AddTile
                row
                locked={isCoAdvisorOnly}
                title={isCoAdvisorOnly ? 'Only the primary advisor can add deals' : undefined}
                label={isCoAdvisorOnly ? 'Only the primary advisor can add deals' : 'Add a deal'}
                onClick={() => setAddDeal(true)}
              />
            </>
          ) : addDeal ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Field
                label="Deal name"
                htmlFor="add-client-deal-name"
                aside={<CloseButton size="sm" label="Remove deal" onClose={() => setAddDeal(false)} />}>
                <TextInput
                  id="add-client-deal-name"
                  placeholder="Name the search, e.g. San Diego coastal home"
                  fullWidth
                  value={dealName}
                  onChange={(e) => setDealName(e.target.value)}
                />
              </Field>
              <Field label="Status">
                <Box role="group" aria-label="Deal status" sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {DEAL_STATUSES.map((s) => (
                    <Chip key={s} on={dealStatus === s} onClick={() => setDealStatus(s)}>
                      <Box
                        component="span"
                        aria-hidden
                        sx={{
                          // the app's tick, as the urgency and listing-status tags carry theirs
                          width: 2,
                          height: 12,
                          borderRadius: '1px',
                          flexShrink: 0,
                          background: STATUS_COLOR[s] || STATUS_COLOR.Search,
                        }}
                      />
                      {s}
                    </Chip>
                  ))}
                </Box>
              </Field>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px 24px' }}>
                <Field label="Period">
                  <Segmented<string>
                    label="Period"
                    size="sm"
                    options={PERIOD_TYPES.map((p) => [p, p] as [string, string])}
                    value={periodType}
                    onChange={(v) => {
                      setPeriodType(v);
                      setFeeOverride(null);
                    }}
                  />
                </Field>
                <Field label="Fee model">
                  <Segmented<DealFeeType>
                    label="Fee model"
                    size="sm"
                    options={[
                      ['Flat', 'Flat fee'],
                      ['Percentage', 'Percentage'],
                    ]}
                    value={feeType}
                    onChange={(nextFeeType) => {
                      setFeeType(nextFeeType);
                      if (nextFeeType === 'Flat') setCommissionPercent('2.5');
                      else setFeeOverride(null);
                    }}
                  />
                </Field>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: pairCols, gap: '12px', alignItems: 'flex-start' }}>
                <Field label="Engagement start">
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      value={engagementStart ? new Date(engagementStart) : null}
                      onChange={(d) => setEngagementStart(d ? d.toISOString().split('T')[0] : '')}
                      slots={{
                        openPickerIcon: () => <Icon name="calendar" size={16} stroke={1.6} />,
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          variant: 'outlined',
                          sx: dsFieldSx,
                        },
                        popper: { sx: pickerPopperSx },
                        openPickerButton: {
                          sx: {
                            borderRadius: '8px',
                            mr: '-8px',
                            color: 'var(--tx-faint)',
                            '&:hover': { backgroundColor: 'var(--tx-fill)', color: 'var(--tx-text)' },
                            '&:focus-visible': { backgroundColor: 'var(--tx-fill)' },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Field>
                {feeType === 'Flat' ? (
                  <Field label="Flat fee" htmlFor="add-client-flat-fee">
                    <TextInput
                      id="add-client-flat-fee"
                      fullWidth
                      value={(feeOverride !== null ? feeOverride : autoFee(periodType)).toLocaleString()}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9]/g, '');
                        const n = parseInt(v, 10);
                        setFeeOverride(isNaN(n) ? 0 : n);
                      }}
                      onFocus={(e) => {
                        if (feeOverride === null) setFeeOverride(autoFee(periodType));
                        e.target.select();
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                      startAdornment={
                        <Box component="span" sx={{ color: 'var(--tx-faint)', fontWeight: 500, mr: '2px' }}>
                          $
                        </Box>
                      }
                      sx={{
                        '& input': {
                          textAlign: 'right',
                          color: feeOverride !== null ? 'var(--tx-text)' : 'var(--tx-muted)',
                        },
                      }}
                    />
                  </Field>
                ) : (
                  <Field label="Commission rate" htmlFor="add-client-commission">
                    <TextInput
                      id="add-client-commission"
                      fullWidth
                      type="text"
                      value={commissionPercent}
                      onChange={(e) => setCommissionPercent(e.target.value)}
                      inputProps={{ inputMode: 'decimal', 'aria-label': 'Commission rate' }}
                      endAdornment={
                        <Box component="span" sx={{ color: 'var(--tx-faint)', fontWeight: 500, ml: '5px' }}>
                          %
                        </Box>
                      }
                      sx={{ '& input': { textAlign: 'right' } }}
                    />
                  </Field>
                )}
              </Box>
            </Box>
          ) : (
            <AddTile
              locked={isCoAdvisorOnly}
              title={isCoAdvisorOnly ? 'Only the primary advisor can add deals' : undefined}
              label={isCoAdvisorOnly ? 'Only the primary advisor can add deals' : 'Add a deal'}
              sub={isCoAdvisorOnly ? undefined : 'No deal attached yet'}
              onClick={() => setAddDeal(true)}
            />
          )}

          {/* Other members of the group (co-buyers, counsel), under the deal: who's in it, beside what they're doing */}
          {divider}
          <SectionLabel>Members</SectionLabel>
          {/* Additional members: each one parted by a hairline, its number as the section label */}
          {extraMembers.map((m, idx) => (
            <Box key={m.id}>
              {divider}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: '-8px',
                  mb: '6px',
                }}>
                <Typography sx={{ fontFamily: font, fontSize: 12, fontWeight: 600, color: 'var(--tx-faint)' }}>
                  Member {idx + 2}
                </Typography>
                <DsIconButton
                  variant="ghost"
                  size="sm"
                  label="Remove member"
                  icon={<Icon name="trash" size={15} stroke={1.6} />}
                  onClick={() => removeExtraMember(m.id)}
                  sx={{ color: 'var(--tx-faint)', mr: '-6px', '&:hover': { color: 'var(--tx-text)' } }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Field label="Full name" htmlFor={`add-client-member-name-${m.id}`}>
                  <TextInput
                    id={`add-client-member-name-${m.id}`}
                    placeholder="Full name"
                    fullWidth
                    value={m.name}
                    onChange={(e) => updateExtraMember(m.id, 'name', e.target.value)}
                  />
                </Field>
                <Box sx={{ display: 'grid', gridTemplateColumns: pairCols, gap: '12px' }}>
                  <Field label="Email" htmlFor={`add-client-member-email-${m.id}`}>
                    <TextInput
                      id={`add-client-member-email-${m.id}`}
                      type="email"
                      icon="mail"
                      placeholder="name@example.com"
                      fullWidth
                      value={m.email}
                      onChange={(e) => updateExtraMember(m.id, 'email', e.target.value)}
                    />
                  </Field>
                  <Field label="Phone" htmlFor={`add-client-member-phone-${m.id}`}>
                    <TextInput
                      id={`add-client-member-phone-${m.id}`}
                      type="tel"
                      icon="phone"
                      placeholder="(555) 555-5555"
                      fullWidth
                      value={m.phone || ''}
                      onChange={(e) => updateExtraMember(m.id, 'phone', normalizePhone(e.target.value))}
                    />
                  </Field>
                </Box>
                <Field label="Role">
                  {/* five roles can outrun a phone's width, so the track scrolls sideways there rather than wrap */}
                  <Box
                    sx={{
                      maxWidth: '100%',
                      overflowX: 'auto',
                      scrollbarWidth: 'none',
                      '&::-webkit-scrollbar': { display: 'none' },
                    }}>
                    <Segmented<string>
                      label={`Member ${idx + 2} role`}
                      size="sm"
                      options={MEMBER_ROLES.map((r) => [r, r] as [string, string])}
                      value={m.role}
                      onChange={(r) => updateExtraMember(m.id, 'role', r)}
                    />
                  </Box>
                </Field>
              </Box>
            </Box>
          ))}

          <Box sx={{ mt: extraMembers.length ? '16px' : '4px' }}>
            <AddTile row label="Add a member" onClick={addExtraMember} />
          </Box>

          {divider}

          <SectionLabel>Notes</SectionLabel>
          <TextInput
            aria-label="Notes"
            placeholder="Internal notes, referral source, context…"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ alignItems: 'flex-start' }}
          />

          {/* Foot: a quiet cancel and the one lime action */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: 'auto', pt: '20px' }}>
            <DsButton variant="ghost" onClick={handleClose}>
              Cancel
            </DsButton>
            <DsButton
              variant="primary"
              disabled={!submitReady}
              onClick={submitReady ? handleSubmit : undefined}
              sx={{ minWidth: 132 }}>
              {isSubmitting ? (isEdit ? 'Saving…' : 'Adding…') : isEdit ? 'Save changes' : 'Add client'}
            </DsButton>
          </Box>
          {apiError && (
            <Typography
              role="alert"
              sx={{
                fontFamily: font,
                fontSize: 12,
                fontWeight: 500,
                color: 'var(--tx-danger)',
                textAlign: 'right',
                mt: '8px',
              }}>
              {apiError}
            </Typography>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
function AdvisorDropdown({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const { users } = useAdminUsers();
  const advisors = users.filter((u) => u.platformRole === 'Advisor');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 0 });

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(t) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(t)
      )
        setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left, minWidth: Math.max(r.width, 200) });
    }
    setOpen((o) => !o);
  };

  const selectedName = value
    ? advisors.find((a) => a.userId === value)?.name ?? 'All Advisors'
    : 'All Advisors';

  return (
    <div
      ref={triggerRef}
      style={{ display: 'inline-flex', alignItems: 'center' }}
      onClick={(e) => e.stopPropagation()}>
      <div
        onClick={handleToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(var(--tx-ink-rgb), 0.05)',
          borderRadius: '12px',
          padding: '8px 14px',
          cursor: 'pointer',
          userSelect: 'none',
          minWidth: 200,
        }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            fontFamily: font,
            color: value ? C.text : 'var(--tx-muted)',
            flex: 1,
          }}>
          {selectedName}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke={C.muted}
          strokeWidth="2.5"
          strokeLinecap="round">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              zIndex: 9999,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '24px',
              padding: '10px 8px',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              minWidth: pos.minWidth,
              boxShadow: 'var(--tx-shadow)',
            }}>
            {[
              { userId: null as string | null, name: 'All Advisors', email: '' },
              ...advisors.map((a) => ({
                userId: a.userId as string | null,
                name: a.name ?? a.email ?? a.userId,
                email: a.email ?? '',
              })),
            ].map((a) => {
              const isSelected = a.userId === value;
              return (
                <div
                  key={a.userId ?? 'all'}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(a.userId);
                    setOpen(false);
                  }}
                  style={{
                    padding: '8px 14px',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                    borderRadius: 10,
                    color: isSelected ? 'var(--tx-text)' : C.text,
                    background: isSelected ? 'var(--tx-fill-2)' : 'transparent',
                    transition: 'background 0.1s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLDivElement).style.background =
                        'rgba(255,255,255,0.06)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                  }}>
                  {a.name}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

export const AdvisorClientsPage = () => {
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');

  const [selectedAdvisor, setSelectedAdvisor] = useState<string | null>(null);

  // Load data from backend — no more SEED_GROUPS
  const { groups, isLoading, error } = useClientGroups(
    canAccessAdmin && selectedAdvisor ? selectedAdvisor : undefined,
  );
  // Mutation hooks used in this component
  const updateGroupMut = useUpdateClientGroup();
  const updateDealMut = useUpdateDeal();
  const updateListingMut = useUpdateListing();
  const createMemberMut = useCreateMember();
  const updateMemberMut = useUpdateMember();
  const deleteMemberMut = useDeleteMember();

  // Property request hooks
  const groupIds = groups.map((g) => g.id);
  const { requestsByGroup } = usePropertyRequestsByGroup(groupIds);
  const resolveRequestMut = useResolvePropertyRequest();
  const [resolvingRequestId, setResolvingRequestId] = useState<string | null>(null);
  const [ucBlockedAddress, setUcBlockedAddress] = useState<string | null>(null);

  const handleApproveRequest = async (requestId: string) => {
    setResolvingRequestId(requestId);
    try {
      await resolveRequestMut.mutateAsync({ id: requestId, action: 'approve' });
      showAlert(t('request.approved'), 'success');
    } catch (err: any) {
      if (err?.response?.data?.code === 'under_contract') {
        setUcBlockedAddress(err?.response?.data?.address ?? 'This property');
        return;
      }
      showAlert(t('request.approve_failed'), 'error');
      throw err;
    } finally {
      setResolvingRequestId(null);
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    setResolvingRequestId(requestId);
    try {
      await resolveRequestMut.mutateAsync({ id: requestId, action: 'reject' });
      showAlert(t('request.rejected'), 'success');
    } catch {
      showAlert(t('request.reject_failed'), 'error');
    } finally {
      setResolvingRequestId(null);
    }
  };

  const [pendingKanbanOffer, setPendingKanbanOffer] = useState<{
    groupId: string;
    dealId: string;
    fromStatus: string;
    toStatus: string;
    groupName: string;
    dealName: string;
  } | null>(null);

  const handleDealStatusChange = (
    groupId: string,
    dealId: string,
    fromStatus: string,
    toStatus: string,
  ) => {
    const group = groups.find((g) => g.id === groupId);
    const deal = group?.deals.find((d) => d.id === dealId);
    if (!group || !deal) return;

    // If moving to an offer-triggering status and no offer logged yet → confirm first
    if (
      DEAL_OFFER_STATUSES.has(toStatus) &&
      deal.offersUsed === 0 &&
      !(deal.listings && deal.listings.length > 0)
    ) {
      setPendingKanbanOffer({
        groupId,
        dealId: deal.id,
        fromStatus,
        toStatus,
        groupName: group.holdingName,
        dealName: deal.name,
      });
      return;
    }

    updateDealMut.mutate({ dealId: deal.id, data: { status: toStatus } });
  };

  const TERMINAL_SET = new Set(['Under Contract', 'Pending', 'Closed', 'Lost', 'Passed']);
  const [pendingKanbanTerminal, setPendingKanbanTerminal] = useState<{
    listingId: string;
    newStatus: string;
    address: string;
  } | null>(null);
  const [kanbanAcceptedPrice, setKanbanAcceptedPrice] = useState('');

  const handleListingStatusChange = (
    groupId: string,
    dealId: string,
    listingId: string,
    newListingStatus: string,
  ) => {
    const group = groups.find((g) => g.id === groupId);
    const deal = group?.deals.find((d) => d.id === dealId);
    const listing = deal?.listings?.find((l) => l.id === listingId);
    if (TERMINAL_SET.has(newListingStatus)) {
      if (newListingStatus === 'Under Contract') setKanbanAcceptedPrice('');
      setPendingKanbanTerminal({
        listingId,
        newStatus: newListingStatus,
        address: listing?.address || deal?.name || '',
      });
      return;
    }
    if (listing && LISTING_OFFER_STATUSES.has(newListingStatus) && listing.offersUsed === 0) {
      setPendingKanbanOffer({
        groupId,
        dealId,
        fromStatus: listing.status,
        toStatus: newListingStatus,
        groupName: group?.holdingName ?? '',
        dealName: listing.address || (deal?.name ?? ''),
      });
      setPendingKanbanListingId(listingId);
      return;
    }
    updateListingMut.mutate({ listingId, data: { status: newListingStatus } });
  };

  const [pendingKanbanListingId, setPendingKanbanListingId] = useState<string | null>(null);

  const confirmKanbanOffer = () => {
    if (!pendingKanbanOffer) return;
    const { dealId, toStatus } = pendingKanbanOffer;
    if (pendingKanbanListingId) {
      // Listing-level offer confirmation — update status and increment offersUsed
      const group = groups.find((g) => g.id === pendingKanbanOffer.groupId);
      const deal = group?.deals.find((d) => d.id === dealId);
      const listing = deal?.listings?.find((l) => l.id === pendingKanbanListingId);
      updateListingMut.mutate({
        listingId: pendingKanbanListingId,
        data: { status: toStatus, offersUsed: (listing?.offersUsed ?? 0) + 1 },
      });
      setPendingKanbanListingId(null);
    } else {
      // Deal-level status change (no listings)
      updateDealMut.mutate({ dealId, data: { status: toStatus } });
    }
    setPendingKanbanOffer(null);
  };
  const [search, setSearch] = useState('');
  // Debounced before hitting the backend — avoids firing a request on every
  // keystroke. Only used for the admin "view all advisors" list below.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  // Admin "view all advisors" mode: real server-side search + pagination
  // (see #1268) instead of the full getAllClientGroups() scan this used to
  // ride on via `groups`. `groups` itself (from useClientGroups above) is
  // kept as the full auto-paginated list for stats/lookups elsewhere on this
  // page (groupIds, activeGroups, event-handler .find() calls) — only the
  // actual rendered list rows below switch to this paginated+searched source.
  const isAdminAllGroupsView = canAccessAdmin && !selectedAdvisor;
  const {
    groups: searchedAdminGroups,
    isLoading: isLoadingSearchedAdminGroups,
    hasNextPage: hasNextGroupsPage,
    isFetchingNextPage: isFetchingNextGroupsPage,
    fetchNextPage: fetchNextGroupsPage,
  } = useAdminClientGroupsSearch({ search: debouncedSearch, enabled: isAdminAllGroupsView });
  // True while an admin-view search result is pending — either the debounce
  // hasn't settled yet or the debounced request is still in flight. Without
  // this, the table would flash "No clients found." for every keystroke
  // before the real (possibly non-empty) result lands. Only meaningful for
  // the admin "view all" case — the non-admin path's `groups` is already
  // fully loaded by the time this component renders (see the isLoading
  // early-return above, from useClientGroups).
  const isAdminSearchPending =
    isAdminAllGroupsView && (search.trim() !== debouncedSearch || isLoadingSearchedAdminGroups);
  const [clientTab, setClientTab] = useState<'active' | 'archived'>('active');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'stats' | 'kanban'>('stats');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);

  const handleGroupSave = (updated: Group) => {
    // Persist group-level fields
    updateGroupMut.mutate(
      {
        id: updated.id,
        data: {
          holdingName: updated.holdingName,
          clientType: updated.clientType,
          notes: updated.notes,
        },
      },
      {
        onSuccess: () => showAlert(t('client.saved'), 'success'),
        onError: () => showAlert(t('client.save_failed'), 'error'),
      },
    );

    // Diff members: create new, update changed, delete removed
    const original = editingGroup ?? groups.find((g) => g.id === updated.id);
    if (original) {
      const originalIds = new Set(original.members.map((m) => m.id));
      const updatedIds = new Set(updated.members.map((m) => m.id));

      // Delete members removed in the drawer
      original.members
        .filter((m) => !updatedIds.has(m.id))
        .forEach((m) => deleteMemberMut.mutate(m.id));

      // Create or update members
      updated.members.forEach((m) => {
        if (!originalIds.has(m.id)) {
          createMemberMut.mutate({ groupId: updated.id, data: m });
        } else {
          updateMemberMut.mutate({ memberId: m.id, data: m });
        }
      });
    }
  };

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const expandAll = () => setExpandedIds(new Set(filtered.map((g) => g.id)));
  const collapseAll = () => setExpandedIds(new Set());

  // Admin "view all" case: holdingName search already ran server-side
  // (searchedAdminGroups) — status + member/deal-name matching stay a
  // client-side refinement over whatever page(s) are loaded, same
  // holdingName-only-server-side tradeoff documented on
  // ClientGroupDBClient.searchClientGroups. Non-admin / specific-advisor
  // case is unchanged (that data source is already small and fully loaded).
  const filtered = (isAdminAllGroupsView ? searchedAdminGroups : groups).filter((g) => {
    if (g.status !== clientTab) return false;
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      g.holdingName.toLowerCase().includes(q) ||
      g.members.some(
        (m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
      ) ||
      g.deals.some((d) => d.name.toLowerCase().includes(q) || d.status.toLowerCase().includes(q))
    );
  });

  const activeGroups = groups.filter((g) => g.status === 'active');

  const sorted = sortKey
    ? [...filtered].sort((a, b) => {
        const va = groupSortValue(a, sortKey);
        const vb = groupSortValue(b, sortKey);
        const cmp =
          typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filtered;

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'transparent',
          fontFamily: font,
          color: C.muted,
          fontSize: 14,
        }}>
        Loading clients…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          background: 'transparent',
          fontFamily: font,
          flexDirection: 'column',
          gap: 12,
        }}>
        <div style={{ color: C.danger, fontSize: 14 }}>Failed to load clients.</div>
        <button
          style={{ ...btnPrimary, height: 36, fontSize: 13 }}
          onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  // The view switch (stats or Kanban) and, for admins, the advisor picker: on a computer they ride in the table's
  // toolbar instead of taking a row of their own above the KPI cards
  const viewControls = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            {/* Advisor dropdown — admin only; nothing renders (and no gap is kept) for advisors */}
            {canAccessAdmin && <AdvisorDropdown value={selectedAdvisor} onChange={setSelectedAdvisor} />}
            <Segmented
              label="Clients view"
              block={isMobile}
              options={[
                ['stats', 'Overview'],
                ['kanban', 'Pipeline'],
              ]}
              value={viewMode}
              onChange={setViewMode}
            />
    </div>
  );

  return (
    <div
      style={{
        fontFamily: font,
        // phone: the frame already sets the gap under the menu bar
        padding: isMobile ? '0 10px 20px' : '20px 0',
        background: 'transparent',
        ...(isMobile
          ? { minHeight: '100dvh', overflowY: 'auto' }
          : { height: '100dvh', overflow: 'hidden' }),
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        scrollbarWidth: 'none' as const,
      }}>
      <style>{`
        input::placeholder { color: #6b6b6b; opacity: 1; }
        @keyframes pulse-ring {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.4); }
        }
        @keyframes pulse-fill {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        @keyframes uc-glow-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(192,255,2,0.55); }
          50% { box-shadow: 0 0 0 5px rgba(192,255,2,0); }
        }
        .heq-listing-row { position: relative; }
        .heq-deal-row, .heq-member-row { position: relative; isolation: isolate; }
        .heq-deal-row::before, .heq-member-row::before {
          content: ''; position: absolute;
          top: 0; bottom: 0; left: -10px; right: -10px;
          border-radius: 12px; background: transparent;
          transition: background 0.12s; z-index: -1;
        }
        
        .heq-group-row { position: relative; isolation: isolate; }
        .heq-group-row::before {
          content: ''; position: absolute;
          top: 0; bottom: 0; left: 0; right: 0;
          border-radius: 12px; background: transparent;
          transition: background 0.12s; z-index: -1;
        }
        @media (min-width: 769px) {

        }
        @media (max-width: 768px) {
          .heq-group-row::before, .heq-deal-row::before, .heq-member-row::before { display: none; }
        }
        .heq-edit-btn { opacity: 0; transition: opacity 0.15s; }
        .heq-group-row:hover .heq-edit-btn, .heq-deal-row:hover .heq-edit-btn, .heq-listing-row:hover .heq-edit-btn { opacity: 1; }
      `}</style>

      {pendingKanbanTerminal &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--tx-backdrop)',
            }}
            onClick={() => setPendingKanbanTerminal(null)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderRadius: '24px',
                padding: '25px',
                width: 480,
                maxWidth: 'calc(100vw - 40px)',
                boxShadow: 'var(--tx-shadow)',
                fontFamily: font,
              }}>
              <div style={{ fontSize: 17, fontWeight: 600, color: C.text, marginBottom: 16 }}>
                {pendingKanbanTerminal.newStatus === 'Closed'
                  ? 'Close Deal'
                  : pendingKanbanTerminal.newStatus === 'Under Contract'
                  ? 'Mark as Under Contract'
                  : pendingKanbanTerminal.newStatus === 'Pending'
                  ? 'Mark as Pending'
                  : pendingKanbanTerminal.newStatus === 'Lost'
                  ? 'Mark as Lost'
                  : 'Mark as Passed'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                  {pendingKanbanTerminal.address.split(',')[0] || 'this listing'}
                </span>
                <span style={{ fontSize: 13, color: C.muted }}>→</span>
                <span
                  style={{
                    background: `${
                      LISTING_STATUS_COLOR[pendingKanbanTerminal.newStatus] || C.muted
                    }18`,
                    color: LISTING_STATUS_COLOR[pendingKanbanTerminal.newStatus] || C.muted,
                    border: 0,
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}>
                  {pendingKanbanTerminal.newStatus}
                </span>
              </div>
              <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5, marginBottom: 24 }}>
                {pendingKanbanTerminal.newStatus === 'Under Contract'
                  ? 'This will mark the property as Under Contract.'
                  : pendingKanbanTerminal.newStatus === 'Pending'
                  ? 'This will mark the property as Pending.'
                  : pendingKanbanTerminal.newStatus === 'Closed'
                  ? 'This will mark the property as Closed (won). The client will retain access.'
                  : "This will revoke the client's access to this property. The listing will remain on the deal as a record."}
              </div>
              {pendingKanbanTerminal.newStatus === 'Under Contract' && (
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      marginBottom: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      color: C.muted,
                      fontFamily: font,
                    }}>
                    Accepted Purchase Price
                  </div>
                  <input
                    aria-label="Accepted purchase price"
                    inputMode="numeric"
                    value={kanbanAcceptedPrice}
                    onChange={(e) => setKanbanAcceptedPrice(formatCurrencyInput(e.target.value))}
                    placeholder="$0"
                    autoFocus
                    style={{ ...inputStyle, width: '100%', height: 40, boxSizing: 'border-box' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setPendingKanbanTerminal(null)}
                  style={{
                    flex: 1,
                    height: 40,
                    borderRadius: 10,
                    background: 'var(--tx-fill)',
                    border: 0,
                    color: C.muted,
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={() => {
                    updateListingMut.mutate({
                      listingId: pendingKanbanTerminal.listingId,
                      data: {
                        status: pendingKanbanTerminal.newStatus,
                        ...(pendingKanbanTerminal.newStatus === 'Under Contract'
                          ? {
                              acceptedPrice: Number(kanbanAcceptedPrice.replace(/[^0-9]/g, '')),
                            }
                          : {}),
                      },
                    });
                    setPendingKanbanTerminal(null);
                  }}
                  disabled={
                    pendingKanbanTerminal.newStatus === 'Under Contract' &&
                    Number(kanbanAcceptedPrice.replace(/[^0-9]/g, '')) <= 0
                  }
                  style={{
                    flex: 2,
                    height: 40,
                    borderRadius: 10,
                    background: LISTING_STATUS_COLOR[pendingKanbanTerminal.newStatus] || C.lime,
                    border: 'none',
                    color: 'var(--tx-text)',
                    fontFamily: font,
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    cursor:
                      pendingKanbanTerminal.newStatus === 'Under Contract' &&
                      Number(kanbanAcceptedPrice.replace(/[^0-9]/g, '')) <= 0
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      pendingKanbanTerminal.newStatus === 'Under Contract' &&
                      Number(kanbanAcceptedPrice.replace(/[^0-9]/g, '')) <= 0
                        ? 0.6
                        : 1,
                  }}>
                  {pendingKanbanTerminal.newStatus === 'Closed'
                    ? 'Confirm close'
                    : pendingKanbanTerminal.newStatus === 'Lost'
                    ? 'Confirm lost'
                    : pendingKanbanTerminal.newStatus === 'Passed'
                    ? 'Confirm passed'
                    : 'Confirm'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {pendingKanbanOffer &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--tx-backdrop)',
            }}
            onClick={() => setPendingKanbanOffer(null)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderRadius: '24px',
                padding: '25px',
                width: 480,
                maxWidth: 'calc(100vw - 40px)',
              }}>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: C.text,
                  marginBottom: 16,
                }}>
                Confirm offer
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                  {(pendingKanbanOffer.dealName || '').split(',')[0]}
                </span>
                <span style={{ fontSize: 13, color: C.muted }}>→</span>
                <span
                  style={{
                    background: `${
                      LISTING_STATUS_COLOR[
                        KANBAN_TO_LISTING[pendingKanbanOffer.toStatus] ||
                          pendingKanbanOffer.toStatus
                      ] || C.muted
                    }18`,
                    color:
                      LISTING_STATUS_COLOR[
                        KANBAN_TO_LISTING[pendingKanbanOffer.toStatus] ||
                          pendingKanbanOffer.toStatus
                      ] || C.muted,
                    border: 0,
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}>
                  {KANBAN_TO_LISTING[pendingKanbanOffer.toStatus] || pendingKanbanOffer.toStatus}
                </span>
              </div>
              <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5, marginBottom: 24 }}>
                This will permanently count as{' '}
                <span style={{ color: C.muted, fontWeight: 600 }}>1 offer used</span> against this
                search period. This cannot be undone.
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setPendingKanbanOffer(null);
                    setPendingKanbanListingId(null);
                  }}
                  style={{
                    flex: 1,
                    height: 40,
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 10,
                    color: C.muted,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Cancel
                </button>
                <button
                  onClick={confirmKanbanOffer}
                  style={{
                    flex: 2,
                    height: 40,
                    background: 'var(--tx-fill-2)',
                    border: 'none',
                    borderRadius: 10,
                    color: 'var(--tx-text)',
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: font,
                    cursor: 'pointer',
                  }}>
                  Confirm offer
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...(isMobile ? {} : { flex: 1, minHeight: 0, overflow: 'hidden' }),
        }}>
        {isMobile && (
          // on the chalk ground the switch sits in a slate tile (a translucent track washes out there)
          <div
            style={{
              marginBottom: 10,
              padding: 4,
              borderRadius: 16,
              background: C.card,
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow-sm)',
            }}>
            {viewControls}
          </div>
        )}
        {viewMode === 'stats' ? (
          <StatCards groups={activeGroups} />
        ) : (
          <KanbanBoard
            groups={activeGroups}
            onDealStatusChange={handleDealStatusChange}
            onListingStatusChange={handleListingStatusChange}
          />
        )}

        {/* Main card */}
        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            ...(isMobile ? {} : { flex: 1, minHeight: 0, overflow: 'hidden' }),
          }}>
          {/* Toolbar: single row — search left, actions right */}
          {(() => {
            const allExpanded = sorted.length > 0 && sorted.every((g) => expandedIds.has(g.id));
            const isArchived = clientTab === 'archived';

            // toolbar icon actions: the design system's tonal IconButton, its label as the tooltip
            const iconBtn = (
              onClick: () => void,
              label: string,
              active: boolean,
              children: React.ReactNode,
            ) => (
              <DsIconButton icon={children} label={label} on={active} onClick={onClick} tipPlacement="bottom" />
            );

            const searchBox = (
              <TextInput
                icon="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients…"
                sx={{ flex: isMobile ? 1 : 'none', width: isMobile ? undefined : 240 }}
              />
            );

            const archiveToggle = iconBtn(
              () => {
                setClientTab(isArchived ? 'active' : 'archived');
                setExpandedIds(new Set());
              },
              isArchived ? 'Back to active' : 'View archived',
              isArchived,
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8" />
                <rect x="1" y="3" width="22" height="5" rx="1" />
                <line x1="10" y1="12" x2="14" y2="12" />
              </svg>,
            );

            const expandToggle = iconBtn(
              allExpanded ? collapseAll : expandAll,
              allExpanded ? 'Collapse all' : 'Expand all',
              false,
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                {allExpanded ? (
                  <>
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="10" y1="14" x2="3" y2="21" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                  </>
                ) : (
                  <>
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </>
                )}
              </svg>,
            );

            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                {!isMobile && viewControls}
                {!isMobile && <div style={{ flex: 1 }} />}
                {searchBox}
                {(isAdvisor || canAccessAdmin) &&
                  !isArchived &&
                  (isMobile ? (
                    iconBtn(
                      () => setDialogOpen(true),
                      'Add Client',
                      false,
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>,
                    )
                  ) : (
                    <DsButton variant="add" onClick={() => setDialogOpen(true)}>
                      Add client
                    </DsButton>
                  ))}
                {archiveToggle}
                {expandToggle}
              </div>
            );
          })()}

          {/* Mobile table header */}
          {isMobile &&
            (() => {
              const mobileCol = (label: string, key: SortKey, align: 'left' | 'right' = 'left') => (
                <div
                  onClick={() => handleSort(key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    cursor: 'pointer',
                    userSelect: 'none',
                    justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
                  }}>
                  <span>{label}</span>
                  <SortCaret active={sortKey === key} dir={sortKey === key ? sortDir : 'asc'} />
                </div>
              );
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    fontSize: 11,
                    fontWeight: 600,
                    color: C.muted,
                    fontFamily: font,
                    ...insetDivider,
                  }}>
                  {mobileCol('Client', 'name')}
                  {mobileCol('Fee', 'fee', 'right')}
                </div>
              );
            })()}

          {/* Table header */}
          {!isMobile &&
            (() => {
              const hStyle: React.CSSProperties = {
                fontSize: 12,
                fontWeight: 600,
                color: C.muted,
                fontFamily: font,
                display: 'flex',
                alignItems: 'center',
              };
              const sortable = (label: string, key: SortKey) => (
                <div
                  style={{ ...hStyle, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort(key)}>
                  {label}
                  <SortCaret active={sortKey === key} dir={sortKey === key ? sortDir : 'asc'} />
                </div>
              );
              return (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: MG,
                    padding: '10px 0',
                    ...insetDivider,
                    gap: 25,
                  }}>
                  <div />
                  {sortable('Client', 'name')}
                  <div />
                  {sortable('Status', 'status')}
                  <div style={hStyle}>Active deals</div>
                  <div style={hStyle}>Offers</div>
                  {sortable('Expires', 'expires')}
                  {sortable('Fee', 'fee')}
                </div>
              );
            })()}

          {/* Group rows */}
          <div
            style={
              isMobile
                ? {}
                : {
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'none',
                    // the clip edge sits 12px outside the columns so an open client's box can reach past them, the way
                    // an open property's does
                    margin: '0 -12px',
                    padding: '0 12px',
                  }
            }>
            {isAdminSearchPending ? (
              <div
                style={{ textAlign: 'center', padding: '48px 0', color: C.subtle, fontSize: 14 }}>
                Loading…
              </div>
            ) : sorted.length === 0 ? (
              <div
                style={{ textAlign: 'center', padding: '48px 0', color: C.subtle, fontSize: 14 }}>
                {clientTab === 'archived' && !search ? 'No archived clients.' : 'No clients found.'}
              </div>
            ) : (
              sorted.map((g, i) => (
                <GroupRow
                  key={g.id}
                  group={g}
                  isOpen={expandedIds.has(g.id)}
                  onToggle={() => toggleExpand(g.id)}
                  isLast={i === sorted.length - 1}
                  onDealUpdate={(dealId, update) => updateDealMut.mutate({ dealId, data: update })}
                  onEdit={() => setEditingGroup(g)}
                  onArchive={() =>
                    updateGroupMut.mutate(
                      { id: g.id, data: { status: g.status === 'active' ? 'archived' : 'active' } },
                      { onError: () => showAlert('Failed to update group status', 'error') },
                    )
                  }
                  pendingRequests={requestsByGroup[g.id]}
                  onApproveRequest={handleApproveRequest}
                  onRejectRequest={handleRejectRequest}
                  isResolvingRequestId={resolvingRequestId}
                />
              ))
            )}
            {isAdminAllGroupsView && hasNextGroupsPage && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <button
                  type="button"
                  onClick={() => fetchNextGroupsPage()}
                  disabled={isFetchingNextGroupsPage}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 6,
                    padding: '8px 16px',
                    color: C.text,
                    fontSize: 13,
                    cursor: isFetchingNextGroupsPage ? 'default' : 'pointer',
                    opacity: isFetchingNextGroupsPage ? 0.6 : 1,
                  }}>
                  {isFetchingNextGroupsPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AddClientDialog
        open={dialogOpen || !!editingGroup}
        onClose={() => {
          setDialogOpen(false);
          setEditingGroup(null);
        }}
        onAdd={() => {
          /* no-op: mutation onSuccess handles list refresh */
        }}
        editGroup={editingGroup ?? undefined}
        onSave={handleGroupSave}
      />

      {/* Under Contract block dialog — shown when advisor tries to add a locked property */}
      {ucBlockedAddress && (
        <Dialog
          open
          onClose={() => setUcBlockedAddress(null)}
          width={420}
          title="Property Under Contract"
          actions={
            <button
              onClick={() => setUcBlockedAddress(null)}
              style={{
                background: UC_LOCK_COLOR,
                color: 'var(--tx-text)',
                border: 'none',
                borderRadius: 8,
                padding: '8px 20px',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: font,
                cursor: 'pointer',
              }}>
              Got it
            </button>
          }>
          <p
            style={{
              fontSize: 13,
              color: 'var(--tx-muted)',
              fontFamily: font,
              margin: 0,
              lineHeight: 1.6,
            }}>
            {ucBlockedAddress} is currently under contract with another client group. It cannot be
            added until the deal falls through.
          </p>
        </Dialog>
      )}
    </div>
  );
};
